import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Animated,
  Easing,
  Image,
  ImageSourcePropType,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  NativeSyntheticEvent,
  NativeScrollEvent,
  Modal,
  Alert,
  GestureResponderEvent,
  Vibration,
  Platform,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { TonyteMapView as WebView, type TonyteMapViewRef } from '../components/TonyteMapView';
type WebViewMessageEvent = { nativeEvent: { data: string } };
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { removeActivityFromFeedCache, useActivityInterested, useFeed } from '../hooks/useFeed';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import { useCityContext } from '../context/CityContext';
import { useDateContext } from '../context/DateContext';
import { getCityCenter, getCityZoom, normalizeCityKey as normalizeCityKeyFromConfig } from '../utils/cities';
import { explainEventRejection, getEventCoordinates as getCoordinates } from '../utils/eventEligibility';
import TonyteBackground from '../components/TonyteBackground';
import PromotionsSection from '../components/PromotionsSection';
import LoadingSpinner from '../components/LoadingSpinner';
import AmbientGlow from '../components/AmbientGlow';
import { Colors, Typography, Spacing, Radius, Shadow } from '../theme';
import type { TonyteActivity, RootStackParamList, MapFocusLocation, UserRole } from '../types';
import { getWebFallbackImage, resolveActivityImageSource, resolveActivityImage } from '../utils/activityImages';
import { deleteEventFromFirestore, ensureInterestedAndAddToCalendar, ensureInterestedAndJoinEventChat, getLumaRegistrationUrl, isEventChatExpiredError, registerOnLumaThen } from '../utils/activityActions';
import { canManageEvent, type ManageEventUser } from '../utils/eventPermissions';
import { geocodeAddress } from '../utils/geocoding';
import { requestNotificationPermission } from '../utils/permissions';
import { showTonyteToast } from '../utils/tonyteToast';
import TonyteModal from '../components/TonyteModal';
import {
  addDays,
  findNextActivityDate,
  isActivityContinuedOnDate,
  isActivityVisibleForSelectedDate,
  parseActivityDate,
  sortActivitiesByStart,
  toLocalDateKey,
} from '../utils/activityDates';
import { useI18n } from '../i18n';
import { useTranslatedActivity } from '../hooks/useTranslatedActivity';
import { isStudiZoneActivity } from '../utils/studiZone';
import { useLocationForMap, useLocationPermission, getLocationSnapshot } from '../hooks/useLocation';
import { showMessage } from 'react-native-flash-message';
import { isEventActivity } from '../utils/activityKind';
import { useTheme } from '../theme/useTheme';
import { useFeedPromotions } from '../hooks/usePromotions';
import type { TonyteTheme } from '../theme/themes';
import { daylightStyle } from '../theme/daylightStyle';
import { CARTO_VOYAGER_TILE_URL, CARTO_VOYAGER_STANDARD_TILE_URL } from '../config/cartoConfig';

interface Props {
  userId: string | null;
  role?: UserRole | null;
  onGoToDiscover?: () => void;
  // One-shot request (e.g. tapping a shared-location chat message) to
  // center the already-mounted map's camera on a specific point and drop a
  // temporary highlighted pin — never triggers an event refetch. `nonce`
  // changes on every request so the effect re-fires even for repeat taps on
  // the same coordinates.
  focusLocation?: MapFocusLocation;
}

const HOSTED_ASSET_BASE = 'https://tonyte.app';
const MAP_CARD_WIDTH = 270;
const MAP_CARD_GAP = 12;
const MAP_CARD_SNAP_INTERVAL = MAP_CARD_WIDTH + MAP_CARD_GAP;
const CAROUSEL_CARD_HEIGHT = 128;
const CONTINUED_CAROUSEL_GROUP_ID = '__continued_events__';
// Extra room for organizers/admins so the edit/delete row has real breathing
// space instead of being squeezed against the card's bottom edge (which,
// combined with overflow:hidden on the card, was clipping the buttons).
const CAROUSEL_CARD_HEIGHT_MANAGE = 160;

function getActivityCityKey(activity: TonyteActivity): string {
  return normalizeCityKeyFromConfig(activity.cityKey || activity.city);
}

function getMarkerTheme(activity: TonyteActivity): 'orange' | 'purple' | 'teal' {
  if (activity.sourceType === 'club' || activity.category === 'nightlife') return 'purple';
  if (activity.sourceType === 'generated_jam_session' || activity.category === 'adventure') return 'teal';
  return 'orange';
}

function spreadOverlappingEventPoints<T extends { latitude: number; longitude: number }>(events: T[]): T[] {
  const groups = new Map<string, T[]>();
  events.forEach(event => {
    const key = `${event.latitude.toFixed(6)},${event.longitude.toFixed(6)}`;
    const group = groups.get(key);
    if (group) group.push(event);
    else groups.set(key, [event]);
  });

  const offsetMeters = 22;
  const metersPerDegreeLat = 111320;

  // Precompute each event's index within its overlap group up front so the
  // map below is O(n) instead of doing an O(group.length) indexOf() scan
  // per event (which made this O(n^2) for venues with many co-located events).
  const indexByEvent = new Map<T, number>();
  groups.forEach(group => {
    if (group.length <= 1) return;
    group.forEach((event, index) => indexByEvent.set(event, index));
  });

  return events.map(event => {
    const key = `${event.latitude.toFixed(6)},${event.longitude.toFixed(6)}`;
    const group = groups.get(key);
    if (!group || group.length <= 1) return event;

    const index = indexByEvent.get(event) ?? 0;
    const angle = (Math.PI * 2 * index) / group.length;
    const latOffset = (Math.sin(angle) * offsetMeters) / metersPerDegreeLat;
    const metersPerDegreeLng = metersPerDegreeLat * Math.max(Math.cos(event.latitude * Math.PI / 180), 0.2);
    const lngOffset = (Math.cos(angle) * offsetMeters) / metersPerDegreeLng;

    return {
      ...event,
      latitude: event.latitude + latOffset,
      longitude: event.longitude + lngOffset,
    };
  });
}

type LeafletMarkerEvent = {
  id: string;
  imageUrl: string;
  latitude: number;
  longitude: number;
  theme: 'orange' | 'purple' | 'teal';
  count: number;
};

type MapEventGroup = {
  id: string;
  events: TonyteActivity[];
  coordinates: { latitude: number; longitude: number };
  sourceGroupId?: string;
};

const MARKER_IMAGE_FIELDS = [
  'markerImageUrl',
  'thumbnailUrl',
  'coverImageUrl',
  'coverPhoto',
  'coverPhotoURL',
  'imageUrl',
  'image',
  'imageURL',
  'photoUrl',
  'posterUrl',
  'poster',
  'mediaUrl',
  'coverUrl',
  'coverImage',
  'backgroundImageUrl',
  'imagePath',
  'coverPhotoPath',
  'storagePath',
  'assetsImage',
  'categoryImage',
] as const;

const MARKER_FALLBACK_IMAGES = {
  club: 'https://firebasestorage.googleapis.com/v0/b/triptribe-8babd.firebasestorage.app/o/event-marker-images%2FTechno_rave_in_warehouse_club_202605090203.jpeg?alt=media&token=0edd9bd8-d9b4-4c94-9e3d-727b221de71f',
  student: 'https://firebasestorage.googleapis.com/v0/b/triptribe-8babd.firebasestorage.app/o/event-marker-images%2FInternational_student_social_party_202605090203.jpeg?alt=media&token=1554d8d8-2623-4abf-b653-15f9d9a5754f',
  outdoor: 'https://firebasestorage.googleapis.com/v0/b/triptribe-8babd.firebasestorage.app/o/event-marker-images%2FMusic_festival_sunset_diverse_crowd_202605090203.jpeg?alt=media&token=d93c1c46-8502-46f5-af8d-ff1a5de55b7d',
  sports: 'https://firebasestorage.googleapis.com/v0/b/triptribe-8babd.firebasestorage.app/o/event-marker-images%2FMusic_festival_sunset_diverse_crowd_202605090203.jpeg?alt=media&token=d93c1c46-8502-46f5-af8d-ff1a5de55b7d',
  food: 'https://firebasestorage.googleapis.com/v0/b/triptribe-8babd.firebasestorage.app/o/event-marker-images%2FWine_tasting_evening_vineyard_lo%E2%80%A6_202605090203.jpeg?alt=media&token=02451a46-58be-4407-b793-b9c95d0bf8ab',
  culture: 'https://firebasestorage.googleapis.com/v0/b/triptribe-8babd.firebasestorage.app/o/event-marker-images%2FCandlelight_concert_in_hall_202605090203.jpeg?alt=media&token=ddb594f9-bbea-495f-979c-72c3b6c96841',
  default: 'https://firebasestorage.googleapis.com/v0/b/triptribe-8babd.firebasestorage.app/o/event-marker-images%2FCafe_night_diverse_people_talking_202605090203.jpeg?alt=media&token=6ff94dfe-d5eb-4df8-9abb-6df0fab602a2',
};

function normalizeMarkerImageUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\/tonyte\.app\/event-images\//i.test(trimmed)) return '';
  if (/^(https?:)?\/\//i.test(trimmed) || /^data:/i.test(trimmed)) return trimmed;
  if (/^gs:\/\//i.test(trimmed)) return '';
  if (/^(\/)?(assets|event-images|eventImages|official-events|officialEvents)\//i.test(trimmed)) {
    if (/^(\/)?event-images\//i.test(trimmed)) return '';
    return encodeURI(`${HOSTED_ASSET_BASE}/${trimmed.replace(/^\/+/, '')}`);
  }
  if (/\.(png|jpe?g|webp|gif)(\?.*)?$/i.test(trimmed)) {
    return encodeURI(`${HOSTED_ASSET_BASE}/${trimmed.replace(/^\/+/, '')}`);
  }
  return '';
}

function getMarkerFallbackImageUrl(activity: TonyteActivity): string {
  const tokenText = [
    activity.title,
    activity.description,
    activity.category,
    activity.sourceType,
    activity.locationName,
    activity.venueName,
    activity.clubName,
    ...(activity.tags || []),
  ].map(value => String(value || '').toLowerCase()).join(' ');

  if (activity.sourceType === 'club' || activity.category === 'nightlife' || /club|party|dj|techno|dance/.test(tokenText)) {
    return MARKER_FALLBACK_IMAGES.club;
  }
  if (/student|erasmus|campus|university/.test(tokenText)) return MARKER_FALLBACK_IMAGES.student;
  if (/hike|walk|tour|park|outdoor|adventure|activities/.test(tokenText)) return MARKER_FALLBACK_IMAGES.outdoor;
  if (/sport|fitness|yoga|run|paintball|lasertag/.test(tokenText)) return MARKER_FALLBACK_IMAGES.sports;
  if (activity.category === 'food' || /food|dinner|brunch|coffee|restaurant|bar/.test(tokenText)) {
    return MARKER_FALLBACK_IMAGES.food;
  }
  if (activity.category === 'culture' || /museum|gallery|art|concert|church|exhibition|tour/.test(tokenText)) {
    return MARKER_FALLBACK_IMAGES.culture;
  }
  return MARKER_FALLBACK_IMAGES.default;
}

function getMarkerImageUrl(activity: TonyteActivity): string {
  const raw = activity as TonyteActivity & Record<string, unknown>;
  for (const field of MARKER_IMAGE_FIELDS) {
    const value = raw[field];
    if (typeof value !== 'string') continue;
    const normalized = normalizeMarkerImageUrl(value);
    if (normalized) return normalized;
  }
  return getMarkerFallbackImageUrl(activity);
}

function buildLeafletHtml(cityLat: number, cityLng: number, cityZoom: number): string {
  return `<!doctype html>
<html>
<head>
  <meta name="viewport" content="initial-scale=1, maximum-scale=1, user-scalable=no, width=device-width" />
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
  <style>
    html, body, #map { margin:0; padding:0; width:100%; height:100%; overflow:hidden; background:#07101d; }
    .leaflet-container { width:100%; height:100%; background:#07101d; font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif; }
    .leaflet-control-container { display:none; }
    /* Applying the filter to the tile PANE (one element wrapping the whole
       tile grid) instead of each .leaflet-tile image individually. A CSS
       filter forces its element onto its own GPU-composited layer; with the
       filter on every tile, each 256px tile became its own independently
       composited/rasterized layer, exposing the sub-pixel gaps between
       adjacent tiles as a visible grid of hairline vertical/horizontal
       seams (most visible in WebView/Chromium rendering — the "grid lines"
       bug). Scoping the filter to the shared tile pane keeps the exact same
       theming (dark/desaturated/hue-shifted tiles) as a single composited
       layer, so the seams disappear. This pane holds only tile imagery —
       markers, popups, and the current-location marker live in separate
       Leaflet panes and are unaffected. */
    .leaflet-tile-pane { filter:brightness(0.72) contrast(1.12) saturate(0.85) sepia(0.08) hue-rotate(165deg); }
    .theme-overlay { position:absolute; inset:0; pointer-events:none; z-index:450; background:rgba(4,18,24,0.14); }
    .theme-overlay::before {
      content:""; position:absolute; inset:0; pointer-events:none;
      background:
        radial-gradient(circle at 82% 14%, rgba(180,230,255,0.018) 0%, rgba(180,230,255,0) 16%),
        radial-gradient(circle at 16% 20%, rgba(38,208,206,0.014) 0%, rgba(38,208,206,0) 26%),
        radial-gradient(circle at 76% 78%, rgba(242,125,38,0.012) 0%, rgba(242,125,38,0) 22%);
    }
    /* ── Lightweight event markers ──────────────────────────────── */
    .tonyte-event-pin { background:transparent !important; border:0 !important; overflow:visible !important; pointer-events:auto !important; cursor:pointer; }
    .tonyte-event-pin__hitbox { position:relative; display:flex; align-items:stretch; justify-content:center; overflow:visible; pointer-events:auto; cursor:pointer; }
    .tonyte-event-pin__wrap { position:relative; pointer-events:auto; cursor:pointer; overflow:visible; }
    .event-marker {
      position:absolute; top:0;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      overflow:hidden;
      border:1.5px solid rgba(255,255,255,0.72);
      background:radial-gradient(circle at 30% 18%, rgba(255,255,255,0.22), transparent 38%),
        linear-gradient(135deg, rgba(242,125,38,0.46), rgba(4,8,15,0.94));
      box-shadow:0 7px 14px rgba(0,0,0,0.28),0 0 9px rgba(242,125,38,0.16),inset 0 1px 0 rgba(255,255,255,0.12);
      pointer-events:auto; cursor:pointer;
      transition:box-shadow 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
      z-index:1;
    }
    .event-marker::after {
      content:"";
      position:absolute;
      inset:0;
      border-radius:inherit;
      background:linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 34%, rgba(0,0,0,0.18) 100%);
      box-shadow:inset 0 0 0 1px rgba(255,255,255,0.12);
      pointer-events:none;
    }
    .event-marker-fallback {
      width:100%; height:100%;
      background:inherit;
      transform:rotate(45deg) scale(1.42);
      pointer-events:none;
    }
    .event-marker-image {
      position:absolute; inset:0;
      width:100%; height:100%;
      object-fit:cover; display:block;
      transform:rotate(45deg) scale(1.42);
      filter:saturate(1.05) contrast(1.05);
      pointer-events:none;
      backface-visibility:hidden;
      -webkit-backface-visibility:hidden;
    }
    .event-marker-tail {
      position:absolute; bottom:0;
      border-radius:999px;
      background:rgba(242,125,38,0.88);
      box-shadow:0 0 5px rgba(242,125,38,0.3),0 4px 9px rgba(0,0,0,0.28);
      pointer-events:none;
    }
    .tonyte-event-pin--teal .event-marker {
      background:radial-gradient(circle at 30% 18%, rgba(255,255,255,0.22), transparent 38%),
        linear-gradient(135deg, rgba(38,208,206,0.46), rgba(4,8,15,0.94));
      box-shadow:0 7px 14px rgba(0,0,0,0.28),0 0 9px rgba(38,208,206,0.16),inset 0 1px 0 rgba(255,255,255,0.12);
    }
    .tonyte-event-pin--teal .event-marker-tail { background:rgba(38,208,206,0.88); box-shadow:0 0 5px rgba(38,208,206,0.3),0 4px 9px rgba(0,0,0,0.28); }
    .tonyte-event-pin--purple .event-marker {
      background:radial-gradient(circle at 30% 18%, rgba(255,255,255,0.22), transparent 38%),
        linear-gradient(135deg, rgba(161,140,209,0.46), rgba(4,8,15,0.94));
      box-shadow:0 7px 14px rgba(0,0,0,0.28),0 0 9px rgba(161,140,209,0.16),inset 0 1px 0 rgba(255,255,255,0.12);
    }
    .tonyte-event-pin--purple .event-marker-tail { background:rgba(161,140,209,0.88); box-shadow:0 0 5px rgba(161,140,209,0.3),0 4px 9px rgba(0,0,0,0.28); }
    .tonyte-event-pin--active .event-marker {
      border-color:rgba(230,255,251,0.94);
      box-shadow:0 8px 16px rgba(0,0,0,0.3),0 0 10px rgba(0,255,200,0.24),inset 0 1px 0 rgba(255,255,255,0.18);
      transform:rotate(-45deg) scale(1.08);
    }
    .event-marker-count {
      position:absolute; left:50%; top:50%;
      width:28px; height:28px; padding:0;
      display:flex; align-items:center; justify-content:center;
      border-radius:999px;
      background:rgba(3,7,15,0.72);
      color:rgba(255,255,255,0.96);
      font-size:14px; line-height:28px; font-weight:800;
      border:1.25px solid rgba(255,255,255,0.68);
      box-shadow:inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 7px rgba(0,0,0,0.32);
      z-index:4;
      pointer-events:none;
      transform:translate(-50%, -50%) rotate(45deg);
      backface-visibility:hidden;
      -webkit-backface-visibility:hidden;
      box-sizing:border-box;
    }
    /* ── Current-location marker: pure CSS, no image decode, visually
       distinct (solid blue dot + soft pulse ring) from the orange/teal/
       purple event pins above. Cheap on Android since it's a single small
       divIcon updated in place via setLatLng, never recreated. ── */
    .tonyte-user-location-pin { background:transparent !important; border:0 !important; pointer-events:none !important; }
    .user-location-dot-wrap { width:22px; height:22px; position:relative; }
    .user-location-pulse {
      position:absolute; inset:0;
      border-radius:50%;
      background:rgba(59,130,246,0.35);
      animation: tonyte-user-pulse 2.2s ease-out infinite;
    }
    .user-location-dot {
      position:absolute; left:50%; top:50%;
      width:13px; height:13px;
      margin:-6.5px 0 0 -6.5px;
      border-radius:50%;
      background:#3b82f6;
      border:2px solid #ffffff;
      box-shadow:0 1px 4px rgba(0,0,0,0.35);
    }
    @keyframes tonyte-user-pulse {
      0% { transform:scale(0.5); opacity:0.55; }
      100% { transform:scale(2.6); opacity:0; }
    }
    /* ── Shared-location highlight: turquoise (Tonyte's "active/selected"
       accent) so it reads as a distinct, temporary highlight — never
       confused with the blue current-location dot or the orange/teal/
       purple event pins. ── */
    .tonyte-shared-location-pin { background:transparent !important; border:0 !important; pointer-events:none !important; }
    .shared-location-dot-wrap { width:30px; height:30px; position:relative; }
    .shared-location-pulse {
      position:absolute; inset:0;
      border-radius:50%;
      background:rgba(38,208,206,0.38);
      animation: tonyte-shared-pulse 1.7s ease-out infinite;
    }
    .shared-location-dot {
      position:absolute; left:50%; top:50%;
      width:17px; height:17px;
      margin:-8.5px 0 0 -8.5px;
      border-radius:50%;
      background:#26D0CE;
      border:2.5px solid #ffffff;
      box-shadow:0 2px 8px rgba(0,0,0,0.4);
    }
    @keyframes tonyte-shared-pulse {
      0% { transform:scale(0.5); opacity:0.65; }
      100% { transform:scale(2.9); opacity:0; }
    }
  </style>
</head>
<body>
  <div id="map"></div>
  <div class="theme-overlay"></div>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
  <script>
    const post = (payload) => window.ReactNativeWebView && window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    const map = L.map('map', { zoomControl:false, attributionControl:false, preferCanvas:false }).setView([${cityLat}, ${cityLng}], ${cityZoom});
    const tileUrl = /Android/i.test(navigator.userAgent)
      ? '${CARTO_VOYAGER_STANDARD_TILE_URL}'
      : '${CARTO_VOYAGER_TILE_URL.replace('@2x', '{r}')}';
    L.tileLayer(tileUrl, {
      maxZoom:19, attribution:'&copy; OpenStreetMap contributors &copy; CARTO'
    }).addTo(map);
    let activeEventId = null;
    const markers = {};
    const esc = (v) => String(v||'').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c]));
    const makeIcon = (event, active) => {
      const W = 58, H = 72, B = 52, T = 8;
      const bL = Math.round((W - B) / 2);
      const tL = Math.round((W - T) / 2);
      const thCls = 'tonyte-event-pin--' + event.theme;
      const actCls = active ? ' tonyte-event-pin--active' : '';
      const imgInner = '<div class="event-marker-fallback"></div>' + (event.imageUrl
        ? '<img class="event-marker-image" src="' + esc(event.imageUrl) + '" onerror="this.style.display=&quot;none&quot;" />'
        : '') + (event.count > 1
        ? '<div class="event-marker-count">' + esc(event.count) + '</div>'
        : '');
      const html =
        '<div class="tonyte-event-pin__hitbox" style="width:' + W + 'px;height:' + H + 'px;">' +
          '<div class="tonyte-event-pin__wrap" style="width:' + W + 'px;height:' + H + 'px;">' +
            '<div class="event-marker" style="left:' + bL + 'px;width:' + B + 'px;height:' + B + 'px;">' + imgInner + '</div>' +
            '<div class="event-marker-tail" style="left:' + tL + 'px;width:' + T + 'px;height:' + T + 'px;"></div>' +
          '</div>' +
        '</div>';
      return L.divIcon({
        className: 'tonyte-event-pin ' + thCls + actCls,
        html: html,
        iconSize: [W, H],
        iconAnchor: [W / 2, H]
      });
    };
    window.setTonyteEvents = (nextEvents) => {
      const nextIds = new Set(nextEvents.map((event) => event.id));
      Object.keys(markers).forEach((id) => {
        if (!nextIds.has(id)) {
          map.removeLayer(markers[id].marker);
          delete markers[id];
        }
      });
      nextEvents.forEach((event) => {
        const existing = markers[event.id];
        if (existing) {
          const changed = existing.event.latitude !== event.latitude ||
            existing.event.longitude !== event.longitude ||
            existing.event.theme !== event.theme ||
            existing.event.imageUrl !== event.imageUrl ||
            existing.event.count !== event.count;
          existing.event = event;
          if (changed) {
            existing.marker.setLatLng([event.latitude, event.longitude]);
            existing.marker.setIcon(makeIcon(event, event.id === activeEventId));
          }
          return;
        }
        const marker = L.marker([event.latitude, event.longitude], { icon: makeIcon(event, event.id === activeEventId), keyboard:false }).addTo(map);
        marker.on('click', () => post({ type:'markerPress', id:event.id }));
        markers[event.id] = { marker, event };
      });
      post({ type:'markersMounted', count: nextEvents.length });
    };
    window.focusTonyteEvent = (id, shouldCenter, latitude, longitude) => {
      activeEventId = id;
      Object.values(markers).forEach(({ marker, event }) => {
        const element = marker.getElement && marker.getElement();
        if (element) {
          element.classList.toggle('tonyte-event-pin--active', event.id === id);
        } else {
          marker.setIcon(makeIcon(event, event.id === id));
        }
      });
      const item = markers[id];
      if (shouldCenter && item) {
        map.setView([item.event.latitude, item.event.longitude], Math.max(map.getZoom(), 13), { animate:true });
      } else if (shouldCenter && Number.isFinite(latitude) && Number.isFinite(longitude)) {
        map.setView([latitude, longitude], Math.max(map.getZoom(), 13), { animate:true });
      }
    };
    window.recenterTonyteMap = () => {
      map.setView([${cityLat}, ${cityLng}], ${cityZoom}, { animate:true });
    };
    let userLocationMarker = null;
    const userLocationIcon = L.divIcon({
      className: 'tonyte-user-location-pin',
      html: '<div class="user-location-dot-wrap"><div class="user-location-pulse"></div><div class="user-location-dot"></div></div>',
      iconSize: [22, 22],
      iconAnchor: [11, 11],
    });
    window.setTonyteUserLocation = (latitude, longitude) => {
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
      if (userLocationMarker) {
        userLocationMarker.setLatLng([latitude, longitude]);
      } else {
        userLocationMarker = L.marker([latitude, longitude], { icon: userLocationIcon, keyboard:false, interactive:false, zIndexOffset:-1000 }).addTo(map);
      }
    };
    window.clearTonyteUserLocation = () => {
      if (userLocationMarker) {
        map.removeLayer(userLocationMarker);
        userLocationMarker = null;
      }
    };
    window.focusUserLocation = (latitude, longitude) => {
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
      map.setView([latitude, longitude], Math.max(map.getZoom(), 15), { animate:true });
    };
    let sharedLocationMarker = null;
    const sharedLocationIcon = L.divIcon({
      className: 'tonyte-shared-location-pin',
      html: '<div class="shared-location-dot-wrap"><div class="shared-location-pulse"></div><div class="shared-location-dot"></div></div>',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
    window.setTonyteSharedLocation = (latitude, longitude) => {
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
      if (sharedLocationMarker) {
        sharedLocationMarker.setLatLng([latitude, longitude]);
      } else {
        sharedLocationMarker = L.marker([latitude, longitude], { icon: sharedLocationIcon, keyboard:false, interactive:false, zIndexOffset:900 }).addTo(map);
      }
      map.setView([latitude, longitude], Math.max(map.getZoom(), 16), { animate:true });
    };
    window.clearTonyteSharedLocation = () => {
      if (sharedLocationMarker) {
        map.removeLayer(sharedLocationMarker);
        sharedLocationMarker = null;
      }
    };
    map.whenReady(() => post({ type:'ready' }));
  </script>
</body>
</html>`;
}

function normalizeGroupPart(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getVenueDayGroupKey(activity: TonyteActivity, coordinates: { latitude: number; longitude: number }, dateKey: string): string {
  const raw = activity as TonyteActivity & Record<string, unknown>;
  const venueId = normalizeGroupPart(activity.venueId || raw.primaryHostId);
  if (venueId) return `${dateKey}:venue:${venueId}`;

  const venueName = normalizeGroupPart(activity.venueName || activity.locationName || activity.clubName);
  const address = normalizeGroupPart(activity.address || activity.location);
  const coordinateKey = `${coordinates.latitude.toFixed(5)},${coordinates.longitude.toFixed(5)}`;
  return `${dateKey}:fallback:${venueName || 'venue'}:${address || coordinateKey}`;
}

function getGroupStartTime(group: MapEventGroup): number {
  return parseActivityDate(group.events[0]).startAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
}

// ─── Premium metallic-glass card chrome ────────────────────────────────────
// Shared by MapPreviewCard and MapVenueGroupCard. Everything here is either
// a static gradient (zero repaint cost) or a transform/opacity-only Animated
// value with useNativeDriver: true (runs on the native UI thread, not the JS
// thread, so it costs nothing during map/list scrolling). The animated shine
// sweep only ever runs on the single focused/active card — every other
// visible card renders the same static metallic look with no animation at
// all, which is what keeps this safe on long lists and slower Android
// devices.

const METALLIC_BORDER_COLORS: string[] = [
  'rgba(255,255,255,0.38)',
  'rgba(214,196,168,0.16)',
  'rgba(255,255,255,0.06)',
  'rgba(255,214,170,0.22)',
  'rgba(255,255,255,0.34)',
];

const METALLIC_BORDER_COLORS_ACTIVE: string[] = [
  'rgba(255,255,255,0.55)',
  'rgba(251,191,120,0.32)',
  'rgba(255,255,255,0.14)',
  'rgba(251,146,60,0.38)',
  'rgba(255,255,255,0.5)',
];

const PEARL_BORDER_COLORS: string[] = [
  'rgba(255,255,255,0.92)',
  'rgba(194,166,133,0.34)',
  'rgba(255,252,246,0.72)',
  'rgba(218,176,127,0.40)',
  'rgba(255,255,255,0.88)',
];

const PEARL_BORDER_COLORS_ACTIVE: string[] = [
  'rgba(255,255,255,0.98)',
  'rgba(242,125,38,0.46)',
  'rgba(255,248,237,0.80)',
  'rgba(208,140,72,0.48)',
  'rgba(255,255,255,0.94)',
];

const CARD_SHINE_DURATION_MS = 9500;

/** Slow diagonal light sweep — only animates while `active`. */
const MetallicShine: React.FC<{ active: boolean; width: number; height: number }> = React.memo(({ active, width, height }) => {
  const shineAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!active) return undefined;
    shineAnim.setValue(0);
    const loop = Animated.loop(
      Animated.timing(shineAnim, {
        toValue: 1,
        duration: CARD_SHINE_DURATION_MS,
        easing: Easing.inOut(Easing.sin),
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [active, shineAnim]);

  if (!active) return null;

  const translateX = shineAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-width * 0.9, width * 1.3],
  });

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        cardChromeStyles.shine,
        { width: width * 0.4, height: height * 1.8, transform: [{ translateX }, { rotate: '20deg' }] },
      ]}
    >
      <LinearGradient
        colors={['rgba(255,255,255,0)', 'rgba(255,255,255,0.14)', 'rgba(255,255,255,0)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={StyleSheet.absoluteFill}
      />
    </Animated.View>
  );
});

/** Static glass-depth layers: top highlight + soft inner glow. No animation, no cost. */
const GlassDepthLayers: React.FC = React.memo(() => {
  const { theme } = useTheme();
  return <>
    <LinearGradient
      pointerEvents="none"
      colors={theme.isDark
        ? ['rgba(255,255,255,0.10)', 'rgba(255,255,255,0)']
        : ['rgba(255,255,255,0.42)', 'rgba(255,255,255,0)']}
      start={{ x: 0.15, y: 0 }}
      end={{ x: 0.6, y: 0.5 }}
      style={cardChromeStyles.topHighlight}
    />
    <LinearGradient
      pointerEvents="none"
      colors={theme.isDark
        ? ['rgba(255,255,255,0.05)', 'rgba(255,255,255,0)', 'rgba(0,0,0,0.12)']
        : ['rgba(255,255,255,0.24)', 'rgba(255,252,246,0)', 'rgba(105,76,48,0.10)']}
      locations={[0, 0.5, 1]}
      style={StyleSheet.absoluteFill}
    />
  </>;
});

/** Outer gradient "frame" that reads as a polished metallic border — a 1.2px
 * gradient ring behind the card's real (dark) background, the standard
 * cheap technique for a gradient border with plain Views (no extra library,
 * no masking). Lifts/scales smoothly on focus via native-driver transforms. */
const MetallicCardFrame: React.FC<{
  active: boolean;
  borderRadius: number;
  style?: object;
  children: React.ReactNode;
}> = ({ active, borderRadius, style, children }) => {
  const { theme } = useTheme();
  const focusAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.spring(focusAnim, {
      toValue: active ? 1 : 0,
      useNativeDriver: true,
      speed: 16,
      bounciness: 6,
    }).start();
  }, [active, focusAnim]);

  const translateY = focusAnim.interpolate({ inputRange: [0, 1], outputRange: [0, -2.5] });
  const scale = focusAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.02] });

  // The inner card needs overflow:hidden to clip its image to rounded
  // corners, which also clips any shadow applied to it — so the ambient
  // shadow lives here instead, on the un-clipped outer wrapper.
  return (
    <Animated.View
      style={[
        cardChromeStyles.shadowWrap,
        !theme.isDark && theme.shadows.card,
        active && cardChromeStyles.shadowWrapActive,
        { transform: [{ translateY }, { scale }] },
        style,
      ]}
    >
      <LinearGradient
        colors={theme.isDark
          ? (active ? METALLIC_BORDER_COLORS_ACTIVE : METALLIC_BORDER_COLORS)
          : (active ? PEARL_BORDER_COLORS_ACTIVE : PEARL_BORDER_COLORS)}
        start={{ x: 0.1, y: 0 }}
        end={{ x: 0.95, y: 1 }}
        style={[cardChromeStyles.frame, { borderRadius: borderRadius + 1.2, padding: 1.2 }]}
      >
        {children}
      </LinearGradient>
    </Animated.View>
  );
};

/** Small metallic-gradient icon button with a soft press-scale + a light
 * vibration tap (no haptics library is installed in this app — Vibration is
 * RN core, zero new native dependencies — a real Taptic-engine feel would
 * need e.g. react-native-haptic-feedback added and rebuilt natively). */
const MetallicIconButton: React.FC<{
  onPress: () => void;
  children: React.ReactNode;
  style?: object;
}> = ({ onPress, children, style }) => {
  const { theme } = useTheme();
  const pressAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = useCallback(() => {
    Animated.spring(pressAnim, { toValue: 0.88, useNativeDriver: true, speed: 30, bounciness: 0 }).start();
  }, [pressAnim]);
  const handlePressOut = useCallback(() => {
    Animated.spring(pressAnim, { toValue: 1, useNativeDriver: true, speed: 20, bounciness: 8 }).start();
  }, [pressAnim]);
  // Every usage of this button is nested inside a card's own TouchableOpacity
  // (select/navigate) — stop propagation here so tapping the icon never also
  // fires the parent card's press.
  const handlePress = useCallback((event: GestureResponderEvent) => {
    event.stopPropagation();
    try {
      // Purely a nice-to-have tap feel — must never block/crash the actual
      // action if the platform denies it (e.g. missing VIBRATE permission).
      Vibration.vibrate(8);
    } catch (error) {
      console.warn('[MetallicIconButton] vibrate failed', error);
    }
    onPress();
  }, [onPress]);

  return (
    <Animated.View style={{ transform: [{ scale: pressAnim }] }}>
      <TouchableOpacity
        activeOpacity={0.9}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onPress={handlePress}
        style={style}
      >
        <LinearGradient
          colors={theme.isDark
            ? ['rgba(255,255,255,0.16)', 'rgba(255,255,255,0.02)']
            : theme.gradients.control}
          start={{ x: 0.2, y: 0 }}
          end={{ x: 0.8, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        {children}
      </TouchableOpacity>
    </Animated.View>
  );
};

const cardChromeStyles = StyleSheet.create({
  shadowWrap: {
    ...Shadow.card,
  },
  shadowWrapActive: {
    shadowColor: Colors.sunset,
    shadowOpacity: 0.3,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 6 },
    elevation: 10,
  },
  frame: {
    overflow: 'hidden',
  },
  shine: {
    position: 'absolute',
    top: -20,
    left: 0,
  },
  topHighlight: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '55%',
  },
});

const MapPreviewCard = React.memo(function MapPreviewCard({
  activity,
  active,
  onPress,
  canManage,
  onEdit,
  onDelete,
  userId,
}: {
  activity: TonyteActivity;
  active: boolean;
  onPress: (activity: TonyteActivity) => void;
  canManage: boolean;
  onEdit: (activity: TonyteActivity) => void;
  onDelete: (activity: TonyteActivity) => void;
  userId: string | null;
}) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createMapCarouselThemeStyles(theme), [theme]);
  const translatedActivity = useTranslatedActivity(activity);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [imageSource, setImageSource] = useState<ImageSourcePropType | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [usedImageFallback, setUsedImageFallback] = useState(false);
  const [showInterestPrompt, setShowInterestPrompt] = useState(false);
  const resolvedEventId = getResolvedEventId(activity);
  const { interested, toggle, syncInterested } = useActivityInterested(resolvedEventId, userId, activity);
  // Same gate EventDetailScreen uses to decide whether "mark interested" should
  // first offer to join the event chat — community plans and club events don't
  // have an event chat to join.
  const supportsEventChat = activity.entryType === 'official_event' && activity.sourceType !== 'club';

  // onPress/onEdit/onDelete are stable, activity-taking callbacks shared by
  // every card (see renderMapCard below) so that changing which card is
  // selected doesn't recreate per-card closures and break memoization for
  // every other visible card.
  const handlePress = useCallback(() => onPress(activity), [onPress, activity]);
  const handleEdit = useCallback(() => onEdit(activity), [onEdit, activity]);
  const handleDelete = useCallback(() => onDelete(activity), [onDelete, activity]);

  const handleChat = useCallback(async () => {
    if (!userId || !supportsEventChat) return;
    try {
      await ensureInterestedAndJoinEventChat(activity, userId, interested, t);
      syncInterested(true);
      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
      navigation.navigate('ChatDetail', {
        chatId: resolvedEventId,
        name: translatedActivity.title || activity.title,
        kind: 'event',
      });
    } catch (error) {
      console.warn('[Tonyte][RN][EventChat] join action failed', {
        action: 'ensureInterestedAndJoinEventChat',
        activityId: activity.id,
        resolvedEventId,
        userId,
        error,
      });
      if (isEventChatExpiredError(error)) {
        showTonyteToast(t('event.chatNoLongerAvailable'), 'info', t('event.chatNoLongerAvailableBody'));
      } else {
        showTonyteToast(t('event.couldNotJoinChat'), 'error', t('common.tryAgain'));
      }
    }
  }, [activity, interested, navigation, resolvedEventId, supportsEventChat, syncInterested, t, translatedActivity.title, userId]);

  const handleAddToCalendar = useCallback(async () => {
    if (!userId) return;
    if (await ensureInterestedAndAddToCalendar(activity, userId, t)) syncInterested(true);
  }, [activity, syncInterested, t, userId]);

  const handleInterestPress = useCallback(() => {
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('auth.signInToUseFeature'));
      return;
    }
    if (interested || !supportsEventChat) {
      void toggle();
      return;
    }
    setShowInterestPrompt(true);
  }, [interested, supportsEventChat, t, toggle, userId]);

  useEffect(() => {
    let cancelled = false;
    setImageFailed(false);
    setUsedImageFallback(false);
    resolveActivityImageSource(activity)
      .then(source => {
        if (!cancelled) setImageSource(source);
      })
      .catch(() => {
        if (!cancelled) setImageFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activity]);

  const cardHeight = canManage ? CAROUSEL_CARD_HEIGHT_MANAGE : CAROUSEL_CARD_HEIGHT;
  return (
    <>
    <MetallicCardFrame active={active} borderRadius={18} style={styles.carouselFrameSpacing}>
      <TouchableOpacity
        activeOpacity={0.94}
        style={[styles.carouselCard, appearance.carouselCard, { height: cardHeight }]}
        onPress={handlePress}
      >
        {imageSource && !imageFailed ? (
          <Image
            source={imageSource}
            style={styles.carouselImage}
            resizeMode="cover" resizeMethod="resize"
            onError={() => {
              // Same fallback as EventCard.tsx/EventDetailScreen.tsx — a
              // resolved URL can 404 or (confirmed live for tonyte.app's
              // hosted /event-images/ paths) return HTTP 200 with an HTML
              // page instead of the real file. This map carousel card has
              // its own separate image-resolution/render logic (not
              // EventCard.tsx), so it needed the exact same fallback wired
              // in separately.
              if (!usedImageFallback) {
                setUsedImageFallback(true);
                setImageSource({ uri: getWebFallbackImage(activity) });
              } else {
                setImageFailed(true);
              }
            }}
          />
        ) : (
          <LinearGradient
            colors={theme.isDark ? ['#18283a', '#101722', '#070b14'] : theme.eventCard.fallbackGradient}
            style={styles.carouselImage}
          />
        )}
        <LinearGradient
          colors={theme.isDark
            ? ['rgba(3,7,15,0.08)', 'rgba(3,7,15,0.22)', 'rgba(3,7,15,0.58)', 'rgba(3,7,15,0.94)']
            : ['rgba(45,31,22,0.04)', 'rgba(45,31,22,0.20)', 'rgba(38,25,18,0.70)', 'rgba(28,18,13,0.96)']}
          locations={[0, 0.28, 0.58, 1]}
          style={StyleSheet.absoluteFill}
        />
        <GlassDepthLayers />
        <MetallicShine active={active} width={MAP_CARD_WIDTH} height={cardHeight} />
        <View style={styles.carouselContent}>
          <View style={styles.carouselTopRow}>
            <View style={[styles.carouselTypeChip, appearance.carouselTypeChip]}>
              <Text style={[styles.carouselType, appearance.carouselType]} numberOfLines={1}>
                {activity.entryType === 'community_plan' ? t('event.communityPlan') : t('event.officialEvent')}
              </Text>
            </View>
            <MetallicIconButton
              style={[styles.carouselIconBtn, appearance.carouselIconBtn]}
              onPress={() => handleInterestPress()}
            >
              <Icon name="heart" size={11} color={interested ? Colors.turquoise : (theme.isDark ? 'rgba(255,255,255,0.75)' : theme.colors.mediaActionIcon)} />
            </MetallicIconButton>
          </View>
          <View>
            <Text style={[styles.carouselTitle, appearance.carouselTitle]} numberOfLines={1}>{translatedActivity.translatingTitle ? '...' : translatedActivity.title}</Text>
            <Text style={[styles.carouselTime, appearance.carouselTime]} numberOfLines={1}>{activity.startTime || t('event.tonight')}</Text>
            <View style={styles.carouselLocationRow}>
              <Icon name="map-pin" size={9} color="rgba(242,125,38,0.75)" />
              <Text style={[styles.carouselLocation, appearance.carouselLocation]} numberOfLines={1}>
                {activity.venueName || activity.locationName || activity.address || activity.city}
              </Text>
            </View>
          </View>
          {canManage ? (
            <View style={styles.mapManageActions}>
              <MetallicIconButton style={[styles.mapManageButton, appearance.mapManageButton]} onPress={handleEdit}>
                <Text style={[styles.mapManageButtonText, appearance.mapManageButtonText]}>{t('common.edit')}</Text>
              </MetallicIconButton>
              <MetallicIconButton style={[styles.mapManageButton, appearance.mapManageButton]} onPress={handleDelete}>
                <Text style={[styles.mapManageButtonText, styles.mapManageDeleteText]}>{t('common.delete')}</Text>
              </MetallicIconButton>
            </View>
          ) : null}
        </View>
      </TouchableOpacity>
    </MetallicCardFrame>

    <TonyteModal
      visible={showInterestPrompt}
      title={t('event.joinEventChatQuestion')}
      message={getLumaRegistrationUrl(activity) ? `${t('event.joinEventChatPrompt')}\n\n${t('event.registerOnLumaFirst')}` : t('event.joinEventChatPrompt')}
      icon="message-circle"
      iconColor={Colors.turquoise}
      buttons={[
        { label: t('common.cancel'), variant: 'secondary', onPress: () => setShowInterestPrompt(false) },
        {
          label: t('event.interestedOnly'),
          variant: 'secondary',
          onPress: () => { setShowInterestPrompt(false); void registerOnLumaThen(activity, toggle, t); },
        },
        {
          label: t('event.addToCalendar'),
          variant: 'teal',
          onPress: () => { setShowInterestPrompt(false); void registerOnLumaThen(activity, handleAddToCalendar, t); },
        },
        {
          label: t('event.joinChat'),
          variant: 'primary',
          onPress: () => { setShowInterestPrompt(false); void registerOnLumaThen(activity, handleChat, t); },
        },
      ]}
      onDismiss={() => setShowInterestPrompt(false)}
    />
    </>
  );
});

const ContinuedMapCarouselCard = React.memo(function ContinuedMapCarouselCard({
  events,
  expanded,
  imageUrls,
  onToggle,
}: {
  events: TonyteActivity[];
  expanded: boolean;
  imageUrls: Record<string, string>;
  onToggle: () => void;
}) {
  return (
    <MetallicCardFrame active={expanded} borderRadius={18} style={styles.carouselFrameSpacing}>
      <TouchableOpacity
        activeOpacity={0.9}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`The Vibe Continues, ${events.length} overnight events`}
        onPress={onToggle}
        style={styles.continuedCarouselCard}
      >
        <LinearGradient
          colors={['#08252C', '#162536', '#3A2018']}
          locations={[0, 0.52, 1]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.continuedCardGlow} />
        <View style={styles.continuedCardTopRow}>
          <View style={styles.continuedCardLabel}>
            <Icon name="star" size={10} color="#FFD29F" />
            <Text style={styles.continuedCardLabelText}>AFTER MIDNIGHT</Text>
          </View>
          <View style={styles.continuedCardCount}>
            <Text style={styles.continuedCardCountText}>{events.length}</Text>
          </View>
        </View>
        <View style={styles.continuedCardBottomRow}>
          <View style={styles.continuedCardCopy}>
            <Text style={styles.continuedCardTitle}>The Vibe Continues</Text>
            <Text style={styles.continuedCardHint}>{expanded ? 'Tap to minimise' : 'Tap to see what is still going'}</Text>
          </View>
          <View style={styles.continuedCardImages}>
            {events.slice(0, 3).map((activity, index) => {
              const uri = imageUrls[activity.id] || getMarkerImageUrl(activity);
              return uri ? <Image key={activity.id} source={{ uri }} style={[styles.continuedCardImage, index > 0 && styles.continuedCardImageOverlap]} /> : null;
            })}
          </View>
          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color="#FFFFFF" />
        </View>
      </TouchableOpacity>
    </MetallicCardFrame>
  );
});

const MapVenueGroupCard = React.memo(function MapVenueGroupCard({
  group,
  active,
  onPressGroup,
  onPressEvent,
  canManageEventItem,
  onEdit,
  onDelete,
}: {
  group: MapEventGroup;
  active: boolean;
  onPressGroup: (group: MapEventGroup) => void;
  onPressEvent: (activity: TonyteActivity, group: MapEventGroup) => void;
  canManageEventItem: (activity: TonyteActivity) => boolean;
  onEdit: (activity: TonyteActivity) => void;
  onDelete: (activity: TonyteActivity) => void;
}) {
  const { t, tp } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createMapCarouselThemeStyles(theme), [theme]);
  const primary = group.events[0];
  const translatedPrimary = useTranslatedActivity(primary);
  const [imageSource, setImageSource] = useState<ImageSourcePropType | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [usedImageFallback, setUsedImageFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setImageFailed(false);
    setUsedImageFallback(false);
    resolveActivityImageSource(primary)
      .then(source => {
        if (!cancelled) setImageSource(source);
      })
      .catch(() => {
        if (!cancelled) setImageFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [primary]);

  const handleGroupPress = useCallback(() => onPressGroup(group), [group, onPressGroup]);

  return (
    <MetallicCardFrame active={active} borderRadius={18} style={styles.carouselFrameSpacing}>
      <TouchableOpacity
        activeOpacity={0.94}
        style={[styles.groupCarouselCard, appearance.carouselCard]}
        onPress={handleGroupPress}
      >
        {imageSource && !imageFailed ? (
          <Image
            source={imageSource}
            style={styles.groupCardImage}
            resizeMode="cover" resizeMethod="resize"
            onError={() => {
              if (!usedImageFallback) {
                setUsedImageFallback(true);
                setImageSource({ uri: getWebFallbackImage(primary) });
              } else {
                setImageFailed(true);
              }
            }}
          />
        ) : (
          <LinearGradient
            colors={theme.isDark ? ['#18283a', '#101722', '#070b14'] : theme.eventCard.fallbackGradient}
            style={styles.groupCardImage}
          />
        )}
        <LinearGradient
          colors={theme.isDark
            ? ['rgba(3,7,15,0.14)', 'rgba(3,7,15,0.5)', 'rgba(3,7,15,0.82)', 'rgba(3,7,15,0.97)']
            : ['rgba(45,31,22,0.08)', 'rgba(45,31,22,0.30)', 'rgba(45,31,22,0.66)', 'rgba(38,26,19,0.88)']}
          locations={[0, 0.32, 0.6, 1]}
          style={StyleSheet.absoluteFill}
        />
        <GlassDepthLayers />
        <MetallicShine active={active} width={MAP_CARD_WIDTH} height={180} />
        <View style={styles.groupCardContent}>
          <View style={styles.groupCardHeader}>
            <View style={styles.groupCardTitleBlock}>
              <View style={[styles.carouselTypeChip, appearance.carouselTypeChip]}>
                <Text style={[styles.carouselType, appearance.carouselType]} numberOfLines={1}>
                  {t('event.officialEvent')} - {tp('event.eventsCount', group.events.length)}
                </Text>
              </View>
              <Text style={styles.carouselTitle} numberOfLines={1}>
                {translatedPrimary.translatingTitle ? '...' : (primary.venueName || primary.locationName || primary.clubName || primary.title)}
              </Text>
            </View>
            <View style={[styles.groupCountPill, appearance.groupCountPill]}>
              <Text style={styles.groupCountText}>{group.events.length}</Text>
            </View>
          </View>
          <View style={styles.groupEventStack}>
            {group.events.map(activity => (
              <MapVenueGroupEventRow
                key={activity.id}
                activity={activity}
                group={group}
                canManage={canManageEventItem(activity)}
                onPress={onPressEvent}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </View>
        </View>
      </TouchableOpacity>
    </MetallicCardFrame>
  );
});

const MapVenueGroupEventRow = React.memo(function MapVenueGroupEventRow({
  activity,
  group,
  canManage,
  onPress,
  onEdit,
  onDelete,
}: {
  activity: TonyteActivity;
  group: MapEventGroup;
  canManage: boolean;
  onPress: (activity: TonyteActivity, group: MapEventGroup) => void;
  onEdit: (activity: TonyteActivity) => void;
  onDelete: (activity: TonyteActivity) => void;
}) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createMapCarouselThemeStyles(theme), [theme]);
  const translated = useTranslatedActivity(activity);
  const handlePress = useCallback(() => onPress(activity, group), [activity, group, onPress]);
  const handleEdit = useCallback(() => onEdit(activity), [activity, onEdit]);
  const handleDelete = useCallback(() => onDelete(activity), [activity, onDelete]);

  return (
    <TouchableOpacity
      activeOpacity={0.86}
      style={[styles.groupEventRow, appearance.groupEventRow]}
      onPress={handlePress}
    >
      <View style={[styles.groupEventTimePill, appearance.groupEventTimePill]}>
        <Text style={[styles.groupEventTimeText, appearance.groupEventTimeText]}>{activity.startTime || t('event.tonight')}</Text>
      </View>
      <View style={styles.groupEventTextBlock}>
        <Text style={[styles.groupEventTitle, appearance.groupEventTitle]} numberOfLines={1}>
          {translated.translatingTitle ? '...' : translated.title}
        </Text>
        <Text style={[styles.groupEventMeta, appearance.groupEventMeta]} numberOfLines={1}>
          {activity.endTime ? `${activity.startTime || ''}-${activity.endTime}` : activity.venueName || activity.locationName || activity.city}
        </Text>
      </View>
      {canManage ? (
        <View style={styles.groupManageActions}>
          <TouchableOpacity activeOpacity={0.82} style={[styles.groupManageButton, appearance.groupManageButton]} onPress={handleEdit}>
            <Icon name="edit-2" size={11} color={theme.isDark ? '#fff' : theme.colors.mediaActionIcon} />
          </TouchableOpacity>
          <TouchableOpacity activeOpacity={0.82} style={[styles.groupManageButton, appearance.groupManageButton]} onPress={handleDelete}>
            <Icon name="trash-2" size={11} color={Colors.sunset} />
          </TouchableOpacity>
        </View>
      ) : null}
    </TouchableOpacity>
  );
});

// Desktop web map: a slide-in detail panel over the map canvas (right edge,
// margin on every side so the map stays visible around it) instead of
// navigating away to the full-screen EventDetail route — see
// openDesktopDetail/closeDesktopDetail in MapScreen below.
const DesktopEventDetailPanel: React.FC<{
  activity: TonyteActivity;
  anim: Animated.Value;
  desktopSurface: string;
  desktopBorder: string;
  userId: string | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (activity: TonyteActivity) => void;
  onDelete: (activity: TonyteActivity) => void;
  onViewFullPage: (activity: TonyteActivity) => void;
}> = ({ activity, anim, desktopSurface, desktopBorder, userId, canManage, onClose, onEdit, onDelete, onViewFullPage }) => {
  const { t, tp } = useI18n();
  const { theme } = useTheme();
  const translatedActivity = useTranslatedActivity(activity);
  const resolvedEventId = getResolvedEventId(activity);
  const { interested, count, toggle } = useActivityInterested(resolvedEventId, userId, activity);
  const isPlan = activity.entryType === 'community_plan';
  const imageUrl = getMarkerImageUrl(activity);

  const handleInterestPress = () => {
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('auth.signInToUseFeature'));
      return;
    }
    void toggle();
  };

  return (
    <Animated.View
      style={[
        styles.desktopDetailPanel,
        {
          backgroundColor: desktopSurface,
          borderColor: desktopBorder,
          opacity: anim,
          transform: [{ translateX: anim.interpolate({ inputRange: [0, 1], outputRange: [24, 0] }) }],
        },
      ]}
    >
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.desktopDetailScroll}>
        <View style={styles.desktopDetailHero}>
          {imageUrl ? (
            <Image source={{ uri: imageUrl }} style={StyleSheet.absoluteFill} resizeMode="cover" resizeMethod="resize" />
          ) : (
            <LinearGradient colors={theme.isDark ? ['#18283a', '#101722', '#070b14'] : theme.eventCard.fallbackGradient} style={StyleSheet.absoluteFill} />
          )}
          <LinearGradient
            colors={['rgba(3,7,15,0.05)', 'rgba(3,7,15,0.15)', 'rgba(3,7,15,0.75)']}
            locations={[0, 0.5, 1]}
            style={StyleSheet.absoluteFill}
          />
          <TouchableOpacity onPress={onClose} style={styles.desktopDetailClose} accessibilityLabel={t('common.close', 'Close')}>
            <Icon name="x" size={16} color="#fff" />
          </TouchableOpacity>
          <View style={styles.desktopDetailHeroChip}>
            <Text style={styles.desktopDetailHeroChipText}>{isPlan ? t('event.communityPlan') : t('event.officialEvent')}</Text>
          </View>
        </View>

        <View style={styles.desktopDetailBody}>
          <Text style={[styles.desktopDetailTitle, { color: theme.colors.textPrimary }]}>
            {translatedActivity.translatingTitle ? '…' : translatedActivity.title}
          </Text>

          <View style={styles.desktopDetailMetaRow}>
            <Icon name="clock" size={13} color="#F58538" />
            <Text style={[styles.desktopDetailMetaText, { color: theme.colors.textSecondary }]} numberOfLines={1}>
              {[activity.startTime, activity.endTime].filter(Boolean).join(' – ') || t('event.tonight')}
            </Text>
          </View>
          <View style={styles.desktopDetailMetaRow}>
            <Icon name="map-pin" size={13} color="#F58538" />
            <Text style={[styles.desktopDetailMetaText, { color: theme.colors.textSecondary }]} numberOfLines={2}>
              {activity.venueName || activity.locationName || activity.address || activity.city}
            </Text>
          </View>

          {translatedActivity.description ? (
            <Text style={[styles.desktopDetailDescription, { color: theme.colors.textSecondary }]}>
              {translatedActivity.translatingDescription ? t('common.loading') : translatedActivity.description}
            </Text>
          ) : null}

          {translatedActivity.tags.length ? (
            <View style={styles.desktopDetailTagRow}>
              {translatedActivity.tags.slice(0, 6).map(tag => (
                <View key={tag} style={[styles.desktopDetailTag, { borderColor: desktopBorder }]}>
                  <Text style={[styles.desktopDetailTagText, { color: theme.colors.textSecondary }]}>{tag.startsWith('#') ? tag : `#${tag}`}</Text>
                </View>
              ))}
            </View>
          ) : null}

          <TouchableOpacity
            onPress={handleInterestPress}
            activeOpacity={0.85}
            style={[
              styles.desktopDetailInterestBtn,
              { borderColor: interested ? '#F58538' : desktopBorder, backgroundColor: interested ? '#F58538' : 'transparent' },
            ]}
          >
            <Icon name="heart" size={15} color={interested ? '#fff' : '#F58538'} />
            <Text style={[styles.desktopDetailInterestText, { color: interested ? '#fff' : '#F58538' }]}>{t('event.interestedBtn')}</Text>
            <Text style={[styles.desktopDetailInterestText, { color: interested ? '#fff' : '#F58538', opacity: 0.75 }]}>{tp('event.interestedCount', count)}</Text>
          </TouchableOpacity>

          {canManage ? (
            <View style={styles.desktopDetailManageRow}>
              <TouchableOpacity onPress={() => onEdit(activity)} style={[styles.desktopDetailManageBtn, { borderColor: desktopBorder }]}>
                <Text style={[styles.desktopDetailManageText, { color: theme.colors.textPrimary }]}>{t('common.edit')}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => onDelete(activity)} style={[styles.desktopDetailManageBtn, { borderColor: desktopBorder }]}>
                <Text style={[styles.desktopDetailManageText, { color: Colors.sunset }]}>{t('common.delete')}</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          <TouchableOpacity onPress={() => onViewFullPage(activity)} style={styles.desktopDetailFullPageLink} activeOpacity={0.75}>
            <Text style={styles.desktopDetailFullPageText}>{t('event.viewDetails', 'View event details')}</Text>
            <Icon name="arrow-right" size={13} color="#F58538" />
          </TouchableOpacity>
        </View>
      </ScrollView>
    </Animated.View>
  );
};

const MapScreen: React.FC<Props> = ({ userId, role, onGoToDiscover, focusLocation }) => {
  if (__DEV__) console.count('[Perf][Render] MapScreen');
  const { t, language } = useI18n();
  const insets = useSafeAreaInsets();
  const { width: viewportWidth } = useWindowDimensions();
  const { theme } = useTheme();
  const dateAppearance = useMemo(() => createMapDateThemeStyles(theme), [theme]);
  const isDesktopWeb = Platform.OS === 'web' && viewportWidth >= 1024;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const mapPerfStartRef = useRef(performance.now());
  const prevFeedEventsRef = useRef<TonyteActivity[] | null>(null);
  useEffect(() => {
    if (!__DEV__) return undefined;
    console.log('[Perf][Map] MapScreen MOUNTED');
    return () => console.log('[Perf][Map] MapScreen UNMOUNTED');
  }, []);
  const { activities: feedEvents, loading } = useFeed();
  useEffect(() => {
    if (!__DEV__ || prevFeedEventsRef.current === feedEvents) return;
    prevFeedEventsRef.current = feedEvents;
    console.log(`[Perf][Map] events object changed in MapScreen: ${(performance.now() - mapPerfStartRef.current).toFixed(1)}ms`, { rawEventCount: feedEvents.length });
  }, [feedEvents]);
  const currentUser: ManageEventUser | null = useMemo(
    () => userId ? { uid: userId, role: role || undefined } : null,
    [role, userId],
  );
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [cardsCollapsed, setCardsCollapsed] = useState(false);
  const [continuedEventsExpanded, setContinuedEventsExpanded] = useState(false);
  // Desktop web only: the slide-in detail panel over the map (replaces
  // navigating to the full-screen EventDetail route for this one surface —
  // see openDesktopDetail below). `desktopDetailAnim` drives its slide/fade;
  // the activity itself stays in state so the panel can finish its close
  // animation with the outgoing event still rendered instead of popping empty.
  const [desktopDetailActivity, setDesktopDetailActivity] = useState<TonyteActivity | null>(null);
  const desktopDetailAnim = useRef(new Animated.Value(0)).current;
  const { selectedDate, setSelectedDate, today, tomorrow, dateMode } = useDateContext();
  const [nowTick, setNowTick] = useState(() => new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const [desktopCategory, setDesktopCategory] = useState<'all' | 'music' | 'social' | 'culture' | 'free'>('all');
  const { selectedCityKey, setSelectedCityKey, visibleCities } = useCityContext();
  const mapRef = useRef<TonyteMapViewRef>(null);
  const carouselRef = useRef<FlatList<MapEventGroup>>(null);
  const pendingCarouselIndexRef = useRef<number | null>(null);
  const [geocodedCoords, setGeocodedCoords] = useState<Record<string, { lat: number; lng: number }>>({});
  const geocodedAttemptsRef = useRef<Set<string>>(new Set());
  const [resolvedMarkerImageUrls, setResolvedMarkerImageUrls] = useState<Record<string, string>>({});
  const markerImageResolutionAttemptsRef = useRef<Set<string>>(new Set());
  const groupedEventsCacheRef = useRef<Map<string, MapEventGroup>>(new Map());

  // Current-location marker + recenter, backed by the app-wide Location
  // Engine (src/services/locationEngine.ts) — a single always-on watcher
  // started once at app launch, independent of this screen's mount/unmount.
  // This screen only reads the engine's live snapshot and pushes it into
  // the WebView via one imperative injectJavaScript call, the same pattern
  // already used for event markers; GPS activity here can never trigger
  // event refetches or React rerenders outside this effect.
  const markerData = useLocationForMap();
  const permissionStatus = useLocationPermission();
  const lastLocationToastRef = useRef<typeof permissionStatus | null>(null);

  useEffect(() => {
    if (!mapReady) return;
    if (markerData) {
      mapRef.current?.injectJavaScript(
        `window.setTonyteUserLocation && window.setTonyteUserLocation(${markerData.latitude}, ${markerData.longitude}); true;`,
      );
    } else if (permissionStatus === 'denied' || permissionStatus === 'blocked' || permissionStatus === 'unavailable') {
      mapRef.current?.injectJavaScript('window.clearTonyteUserLocation && window.clearTonyteUserLocation(); true;');
    }
  }, [mapReady, markerData, permissionStatus]);

  useEffect(() => {
    if (permissionStatus !== 'denied' && permissionStatus !== 'blocked' && permissionStatus !== 'unavailable') return;
    if (lastLocationToastRef.current === permissionStatus) return;
    lastLocationToastRef.current = permissionStatus;
    showMessage({
      message: permissionStatus === 'unavailable' ? t('map.locationUnavailable', 'Location unavailable') : t('map.locationPermissionDenied', 'Location access denied'),
      description: permissionStatus === 'unavailable'
        ? t('map.locationUnavailableHint', 'Could not get a GPS fix right now.')
        : t('map.locationPermissionDeniedHint', 'Showing the map without your current location.'),
      type: 'info',
      duration: 3200,
    });
  }, [permissionStatus, t]);

  // Recenter ONLY animates the camera to the engine's latest known fix — it
  // never restarts GPS, never re-requests permission, and never triggers a
  // rerender of this screen (it reads the snapshot imperatively).
  const handleRecenterToMe = useCallback(() => {
    const snapshot = getLocationSnapshot();
    if (!snapshot.coords) return;
    mapRef.current?.injectJavaScript(
      `window.focusUserLocation && window.focusUserLocation(${snapshot.coords.latitude}, ${snapshot.coords.longitude}); true;`,
    );
  }, []);

  // Shared-location highlight from a chat "shared location" tap — a
  // one-shot camera focus + temporary pin on the ALREADY-mounted map
  // (imperative injectJavaScript, same pattern as user-location above).
  // Never triggers useFeed()/marker refetch: this effect only depends on
  // mapReady + the focusLocation prop, nothing event-related.
  const [activeSharedLocation, setActiveSharedLocation] = useState<MapFocusLocation | null>(null);
  const lastFocusNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!mapReady || !focusLocation) return;
    if (lastFocusNonceRef.current === focusLocation.nonce) return;
    lastFocusNonceRef.current = focusLocation.nonce;
    if (!Number.isFinite(focusLocation.lat) || !Number.isFinite(focusLocation.lng)) {
      showMessage({ message: t('map.invalidSharedLocation', "This location can't be shown right now."), type: 'info' });
      return;
    }
    mapRef.current?.injectJavaScript(
      `window.setTonyteSharedLocation && window.setTonyteSharedLocation(${focusLocation.lat}, ${focusLocation.lng}); true;`,
    );
    setActiveSharedLocation(focusLocation);
  }, [focusLocation, mapReady, t]);

  const handleDismissSharedLocation = useCallback(() => {
    mapRef.current?.injectJavaScript('window.clearTonyteSharedLocation && window.clearTonyteSharedLocation(); true;');
    setActiveSharedLocation(null);
  }, []);

  const selectedMapDate = selectedDate;
  useEffect(() => {
    const timer = setInterval(() => setNowTick(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const dateOptions = useMemo(
    () => Array.from({ length: 30 }, (_, index) => addDays(today, index)),
    [today],
  );
  const cityOptions = useMemo(
    () => visibleCities.map(c => ({ key: c.value, label: c.label })),
    [visibleCities],
  );
  const selectedCityLabel = useMemo(
    () => visibleCities.find(c => c.value === selectedCityKey)?.label || visibleCities[0]?.label || 'Dresden',
    [selectedCityKey, visibleCities],
  );
  const publicEvents = useMemo(
    () => feedEvents.filter(activity => isEventActivity(activity) && !isStudiZoneActivity(activity)),
    [feedEvents],
  );
  const mapPromotions = useFeedPromotions(publicEvents, selectedCityKey);
  const openPromotion = useCallback((promotion: { id: string }) => {
    navigation.navigate('Promotion', { promotionId: promotion.id });
  }, [navigation]);
  const carouselEventsRef = useRef<TonyteActivity[]>([]);
  const carouselEvents = useMemo(
    () => {
      const result = publicEvents
        .filter(activity => {
          if (selectedCityKey && getActivityCityKey(activity) !== selectedCityKey) return false;
          return isActivityVisibleForSelectedDate(activity, selectedMapDate, nowTick);
        })
        .sort(sortActivitiesByStart);
      // The 60s nowTick re-runs this filter; when the visible set is
      // unchanged, keep the previous array so grouping, marker models and the
      // WebView marker re-send downstream don't all re-run for nothing.
      const previous = carouselEventsRef.current;
      if (previous.length === result.length && previous.every((activity, index) => activity === result[index])) {
        return previous;
      }
      carouselEventsRef.current = result;
      if (__DEV__) {
        console.log(`[Perf][Map] date filtering complete: ${(performance.now() - mapPerfStartRef.current).toFixed(1)}ms`, { rawEventCount: feedEvents.length, validEventCount: result.length });
      }
      return result;
    },
    [feedEvents.length, nowTick, publicEvents, selectedCityKey, selectedMapDate],
  );
  useEffect(() => {
    if (loading || dateMode !== 'today' || carouselEvents.length > 0) return;
    const cityEvents = publicEvents.filter(activity => (
      !selectedCityKey || getActivityCityKey(activity) === selectedCityKey
    ));
    const nextDate = findNextActivityDate(cityEvents, today, nowTick, 30);
    if (nextDate) setSelectedDate(nextDate);
  }, [carouselEvents.length, dateMode, loading, nowTick, publicEvents, selectedCityKey, setSelectedDate, today]);
  const mapped = useMemo(() => {
    const result = carouselEvents
      .map(a => {
        if (getCoordinates(a)) return a;
        const gc = geocodedCoords[a.id];
        if (!gc) return null;
        return { ...a, coordinates: { lat: gc.lat, lng: gc.lng } } as TonyteActivity;
      })
      .filter((a): a is TonyteActivity => a !== null);

    if (__DEV__) {
      console.log(`[Perf][Map] coordinate validation complete: ${(performance.now() - mapPerfStartRef.current).toFixed(1)}ms`, { mapEventCount: result.length });
      console.log('[Events] publicEvents:', publicEvents.length);
      console.log('[Events] carouselEvents (city+date filtered):', carouselEvents.length);
      console.log('[Events] valid coordinates (mapped -> map markers):', result.length);
      carouselEvents
        .filter(a => !result.some(r => r.id === a.id))
        .forEach(a => {
          console.log('[Event eligibility]', a.id, explainEventRejection(a, { cityKey: selectedCityKey, selectedDate: selectedMapDate }));
        });
    }

    return result;
  }, [carouselEvents, geocodedCoords, publicEvents.length, selectedCityKey, selectedMapDate]);
  const groupedEvents = useMemo<MapEventGroup[]>(() => {
    const dateKey = toLocalDateKey(selectedMapDate);
    const rawGroups = new Map<string, { events: TonyteActivity[]; coordinates: { latitude: number; longitude: number } }>();

    mapped.forEach(activity => {
      const coordinates = getCoordinates(activity);
      if (!coordinates) return;
      const key = getVenueDayGroupKey(activity, coordinates, dateKey);
      const existing = rawGroups.get(key);
      if (existing) {
        existing.events.push(activity);
        return;
      }
      rawGroups.set(key, { events: [activity], coordinates });
    });

    // Reuse the previous group object (same identity) whenever its sorted
    // event list and coordinates are unchanged, instead of always spreading
    // a fresh object per group. groupedEvents recomputes on every geocoding
    // completion and marker-image resolution batch (independent async
    // effects, each firing many times) — always minting new group objects
    // here defeated React.memo on MapPreviewCard/MapVenueGroupCard for
    // groups that hadn't actually changed, which is what was showing up as
    // hundreds of carousel-card re-renders.
    const previousByKey = groupedEventsCacheRef.current;
    const nextByKey = new Map<string, MapEventGroup>();
    const result: MapEventGroup[] = [];
    rawGroups.forEach((raw, key) => {
      const sortedEvents = [...raw.events].sort(sortActivitiesByStart);
      const previous = previousByKey.get(key);
      const unchanged = Boolean(
        previous &&
        previous.coordinates.latitude === raw.coordinates.latitude &&
        previous.coordinates.longitude === raw.coordinates.longitude &&
        previous.events.length === sortedEvents.length &&
        previous.events.every((activity, index) => activity === sortedEvents[index]),
      );
      const group: MapEventGroup = unchanged ? previous! : { id: key, events: sortedEvents, coordinates: raw.coordinates };
      nextByKey.set(key, group);
      result.push(group);
    });
    groupedEventsCacheRef.current = nextByKey;
    result.sort((left, right) => getGroupStartTime(left) - getGroupStartTime(right));
    if (__DEV__) {
      console.log(`[Perf][Map] venue grouping complete: ${(performance.now() - mapPerfStartRef.current).toFixed(1)}ms`, { uniqueVenueCount: result.length });
    }
    return result;
  }, [mapped, selectedMapDate]);
  const { continuedCarouselGroups, regularCarouselGroups } = useMemo(() => {
    const continued: MapEventGroup[] = [];
    const regular: MapEventGroup[] = [];

    groupedEvents.forEach(group => {
      const currentEvents = group.events.filter(activity => !isActivityContinuedOnDate(activity, selectedMapDate));
      const overnightEvents = group.events.filter(activity => isActivityContinuedOnDate(activity, selectedMapDate));

      if (currentEvents.length) {
        regular.push(currentEvents.length === group.events.length ? group : {
          ...group,
          id: `${group.id}:current`,
          sourceGroupId: group.id,
          events: currentEvents,
        });
      }
      overnightEvents.forEach(activity => {
        continued.push({
          id: `${group.id}:continued:${activity.id}`,
          sourceGroupId: group.id,
          events: [activity],
          coordinates: group.coordinates,
        });
      });
    });

    regular.sort((left, right) => getGroupStartTime(left) - getGroupStartTime(right));

    return { continuedCarouselGroups: continued, regularCarouselGroups: regular };
  }, [groupedEvents, selectedMapDate]);
  const continuedToggleGroup = useMemo<MapEventGroup | null>(() => {
    const first = continuedCarouselGroups[0];
    if (!first) return null;
    return {
      id: CONTINUED_CAROUSEL_GROUP_ID,
      sourceGroupId: first.sourceGroupId || first.id,
      events: continuedCarouselGroups.map(group => group.events[0]),
      coordinates: first.coordinates,
    };
  }, [continuedCarouselGroups]);
  const visibleCarouselGroups = useMemo(
    () => continuedToggleGroup ? [continuedToggleGroup, ...regularCarouselGroups] : regularCarouselGroups,
    [continuedToggleGroup, regularCarouselGroups],
  );
  useEffect(() => {
    const toGeocode = carouselEvents.filter(a => {
      if (getCoordinates(a)) return false;
      if (geocodedAttemptsRef.current.has(a.id)) return false;
      const addr = (a as any).address || (a as any).location;
      return Boolean(addr?.trim());
    });
    if (toGeocode.length === 0) return;
    let cancelled = false;
    const run = async () => {
      for (const activity of toGeocode) {
        if (cancelled) break;
        geocodedAttemptsRef.current.add(activity.id);
        const address = (activity as any).address || (activity as any).location || '';
        const venue = (activity as any).venueName || (activity as any).locationName || '';
        const city = (activity as any).city || '';
        const q = [venue, address, city].filter(Boolean).join(', ');
        try {
          const geocoded = await geocodeAddress(q);
          if (!cancelled && geocoded) {
            setGeocodedCoords(prev => ({ ...prev, [activity.id]: { lat: geocoded.lat, lng: geocoded.lng } }));
          }
        } catch {
          // silently skip
        }
        await new Promise<void>(resolve => { setTimeout(resolve, 1100); });
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [carouselEvents]); // geocodedAttemptsRef intentionally excluded — it's a mutable ref, not reactive state

  useEffect(() => {
    const toResolve = mapped.filter(activity => {
      if (markerImageResolutionAttemptsRef.current.has(activity.id)) return false;
      // Only resolve if all image fields are blocked by normalizeMarkerImageUrl (e.g. gs:// or event-images/)
      const raw = activity as TonyteActivity & Record<string, unknown>;
      let hasImageField = false;
      for (const field of MARKER_IMAGE_FIELDS) {
        const value = raw[field];
        if (typeof value !== 'string') continue;
        const trimmed = value.trim();
        if (!trimmed) continue;
        hasImageField = true;
        if (normalizeMarkerImageUrl(trimmed)) return false; // already has a usable URL
      }
      return hasImageField; // has fields but all were blocked
    });
    if (toResolve.length === 0) return;
    let cancelled = false;
    // Resolutions can complete in a tight burst (no rate-limit delay here,
    // unlike geocoding below). Batch them into one state update every ~120ms
    // instead of one setState (-> full markerEvents recompute + JSON.stringify
    // + injectJavaScript re-send to the WebView) per single resolved image.
    const pending: Record<string, string> = {};
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      const keys = Object.keys(pending);
      if (keys.length === 0) return;
      const batch = { ...pending };
      keys.forEach(key => delete pending[key]);
      setResolvedMarkerImageUrls(prev => ({ ...prev, ...batch }));
    };
    const scheduleFlush = () => {
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(flush, 120);
    };
    const run = async () => {
      for (const activity of toResolve) {
        if (cancelled) break;
        markerImageResolutionAttemptsRef.current.add(activity.id);
        try {
          const resolved = await resolveActivityImage(activity);
          if (!cancelled && typeof resolved.source === 'string' && resolved.source) {
            pending[activity.id] = resolved.source;
            scheduleFlush();
          }
        } catch {
          // silently skip
        }
      }
      if (!cancelled) flush();
    };
    void run();
    return () => {
      cancelled = true;
      if (flushTimer) clearTimeout(flushTimer);
    };
  }, [mapped]); // markerImageResolutionAttemptsRef intentionally excluded — it's a mutable ref

  const selectedEvent = useMemo(
    () => carouselEvents.find(activity => activity.id === selectedEventId) || null,
    [carouselEvents, selectedEventId],
  );
  const markerEvents = useMemo<LeafletMarkerEvent[]>(() => {
    const result = spreadOverlappingEventPoints(groupedEvents
      .map(group => {
        const activity = group.events[0];
        const imageUrl = resolvedMarkerImageUrls[activity.id] ?? getMarkerImageUrl(activity);
        return {
          id: group.id,
          imageUrl,
          latitude: group.coordinates.latitude,
          longitude: group.coordinates.longitude,
          theme: getMarkerTheme(activity),
          count: group.events.length,
        };
      })
      .filter((event): event is LeafletMarkerEvent => event !== null));
    if (__DEV__) {
      console.log(`[Perf][Map] marker models complete: ${(performance.now() - mapPerfStartRef.current).toFixed(1)}ms`, { groupedMarkerCount: result.length });
    }
    return result;
  }, [groupedEvents, resolvedMarkerImageUrls]);
  const mapHtml = useMemo(() => {
    const center = getCityCenter(selectedCityKey);
    const zoom = getCityZoom(selectedCityKey);
    return buildLeafletHtml(center.lat, center.lng, zoom);
  }, [selectedCityKey]);

  useEffect(() => {
    setMapReady(false);
  }, [mapHtml]);

  const sentMarkerJsonRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mapReady) {
      // A fresh WebView (city change / reload) has no markers yet.
      sentMarkerJsonRef.current = null;
      return;
    }
    const markerJson = JSON.stringify(markerEvents).replace(/</g, '\\u003c');
    // Skip re-sending (and Leaflet rebuilding) an identical marker set.
    if (markerJson === sentMarkerJsonRef.current) return;
    sentMarkerJsonRef.current = markerJson;
    mapRef.current?.injectJavaScript(
      `window.setTonyteEvents && window.setTonyteEvents(${markerJson}); true;`,
    );
    if (__DEV__) {
      console.log(`[Perf][Map] first marker mounted (sent to WebView): ${(performance.now() - mapPerfStartRef.current).toFixed(1)}ms from mount`, { count: markerEvents.length });
    }
  }, [mapReady, markerEvents]);

  useEffect(() => {
    if (selectedEventId && !carouselEvents.some(activity => activity.id === selectedEventId)) {
      setSelectedEventId(null);
    }
  }, [carouselEvents, selectedEventId]);
  useEffect(() => {
    if (selectedGroupId && !groupedEvents.some(group => group.id === selectedGroupId)) {
      setSelectedGroupId(null);
    }
  }, [groupedEvents, selectedGroupId]);
  useEffect(() => {
    setContinuedEventsExpanded(false);
  }, [selectedMapDate]);

  useEffect(() => {
    const index = pendingCarouselIndexRef.current;
    if (cardsCollapsed || index === null) return;

    requestAnimationFrame(() => {
      carouselRef.current?.scrollToIndex({
        index,
        animated: true,
        viewPosition: 0.5,
      });
      pendingCarouselIndexRef.current = null;
    });
  }, [cardsCollapsed, visibleCarouselGroups]);

  const focusGroup = useCallback((group: MapEventGroup, options: { centerMap?: boolean; scrollToCard?: boolean; eventId?: string } = {}) => {
    const selectedActivity = options.eventId
      ? group.events.find(activity => activity.id === options.eventId) || group.events[0]
      : group.events[0];
    const sourceGroupId = group.sourceGroupId || group.id;
    setSelectedGroupId(sourceGroupId);
    setSelectedEventId(selectedActivity.id);
    const latitude = group.coordinates.latitude;
    const longitude = group.coordinates.longitude;
    mapRef.current?.injectJavaScript(
      `window.focusTonyteEvent && window.focusTonyteEvent(${JSON.stringify(sourceGroupId)}, ${options.centerMap ? 'true' : 'false'}, ${latitude}, ${longitude}); true;`,
    );
    if (options.scrollToCard) {
      const isContinued = isActivityContinuedOnDate(selectedActivity, selectedMapDate);
      const index = isContinued
        ? visibleCarouselGroups.findIndex(item => item.id === CONTINUED_CAROUSEL_GROUP_ID)
        : visibleCarouselGroups.findIndex(item => item.events.some(activity => activity.id === selectedActivity.id));
      if (index >= 0) {
        pendingCarouselIndexRef.current = index;
        setCardsCollapsed(false);
        if (isContinued) setContinuedEventsExpanded(true);
        if (!cardsCollapsed) {
          carouselRef.current?.scrollToIndex({
            index,
            animated: true,
            viewPosition: 0.5,
          });
          pendingCarouselIndexRef.current = null;
        }
      }
    }
  }, [cardsCollapsed, selectedMapDate, visibleCarouselGroups]);

  const handleCarouselMomentumEnd = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!visibleCarouselGroups.length) return;
    const rawIndex = Math.round(event.nativeEvent.contentOffset.x / MAP_CARD_SNAP_INTERVAL);
    const index = Math.max(0, Math.min(visibleCarouselGroups.length - 1, rawIndex));
    const group = visibleCarouselGroups[index];
    if (group?.id === CONTINUED_CAROUSEL_GROUP_ID) return;
    if (group && (group.sourceGroupId || group.id) !== selectedGroupId) {
      focusGroup(group);
    }
  }, [focusGroup, selectedGroupId, visibleCarouselGroups]);

  const handleMarkerPress = useCallback((groupId: string) => {
    const group = groupedEvents.find(item => item.id === groupId);
    if (group) focusGroup(group, { scrollToCard: true });
  }, [focusGroup, groupedEvents]);

  const handleMapMessage = useCallback((event: WebViewMessageEvent) => {
    try {
      const payload = JSON.parse(event.nativeEvent.data);
      if (payload.type === 'ready') {
        if (__DEV__) {
          console.log(`[Perf][Map] WebView (Leaflet+tiles) ready: ${(performance.now() - mapPerfStartRef.current).toFixed(1)}ms from mount`);
        }
        setMapReady(true);
      }
      if (payload.type === 'markerPress') {
        handleMarkerPress(String(payload.id));
      }
      if (payload.type === 'markersMounted' && __DEV__) {
        console.log(`[Perf][Map] all initial markers mounted: ${(performance.now() - mapPerfStartRef.current).toFixed(1)}ms from mount`, { count: payload.count });
      }
    } catch (error) {
      console.warn('[Tonyte][RN][Map] webview message parse failed', event.nativeEvent.data, error);
    }
  }, [handleMarkerPress]);

  const keyExtractorMapCard = useCallback((group: MapEventGroup) => group.id, []);

  // Stable, activity-taking callbacks shared by every card. Keeping these
  // identities stable (independent of `selectedEventId`) means selecting a
  // card only changes the `active` prop for the affected cards instead of
  // forcing every visible card to recreate its onEdit/onDelete/onPress
  // closures and lose its React.memo bailout.
  const handleEditCard = useCallback((activity: TonyteActivity) => {
    navigation.navigate('UploadEvent', { event: activity });
  }, [navigation]);

  const handleDeleteCard = useCallback((activity: TonyteActivity) => {
    Alert.alert(t('event.deleteEventQuestion'), t('event.deleteEventConfirm'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'),
        style: 'destructive',
        onPress: () => {
          removeActivityFromFeedCache(activity.id);
          void deleteEventFromFirestore(activity, userId || '').catch(() => {
            Alert.alert(t('event.couldNotDeleteEvent'), t('common.tryAgain'));
          });
        },
      },
    ]);
  }, [t, userId]);

  const handlePressCard = useCallback((activity: TonyteActivity) => {
    trackEventDetailOpen({ eventId: getResolvedEventId(activity), organizerId: activity.hostId, source: ANALYTICS_SOURCES.MAP });
    navigation.navigate('EventDetail', { eventId: getResolvedEventId(activity), event: activity });
  }, [navigation]);

  const handlePressGroup = useCallback((group: MapEventGroup) => {
    const activity = group.events[0];
    trackEventDetailOpen({ eventId: getResolvedEventId(activity), organizerId: activity.hostId, source: ANALYTICS_SOURCES.MAP });
    navigation.navigate('EventDetail', { eventId: getResolvedEventId(activity), event: activity });
  }, [navigation]);

  const handlePressGroupedEvent = useCallback((activity: TonyteActivity, group: MapEventGroup) => {
    setSelectedGroupId(group.id);
    setSelectedEventId(activity.id);
    trackEventDetailOpen({ eventId: getResolvedEventId(activity), organizerId: activity.hostId, source: ANALYTICS_SOURCES.MAP });
    navigation.navigate('EventDetail', { eventId: getResolvedEventId(activity), event: activity });
  }, [navigation]);

  const canManageEventItem = useCallback((activity: TonyteActivity) => canManageEvent(currentUser, activity), [currentUser]);

  const desktopEvents = useMemo(() => carouselEvents.filter(activity => {
    if (desktopCategory === 'all') return true;
    if (desktopCategory === 'free') return activity.isFree === true;
    const tokens = `${activity.category || ''} ${(activity.tags || []).join(' ')} ${activity.title || ''}`.toLowerCase();
    return tokens.includes(desktopCategory);
  }), [carouselEvents, desktopCategory]);

  const focusDesktopEvent = useCallback((activity: TonyteActivity) => {
    const group = groupedEvents.find(candidate => candidate.events.some(event => event.id === activity.id));
    if (group) focusGroup(group, { centerMap: true, eventId: activity.id });
  }, [focusGroup, groupedEvents]);

  const openDesktopDetail = useCallback((activity: TonyteActivity) => {
    focusDesktopEvent(activity);
    setDesktopDetailActivity(activity);
  }, [focusDesktopEvent]);

  const closeDesktopDetail = useCallback(() => {
    Animated.timing(desktopDetailAnim, {
      toValue: 0,
      duration: 200,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(() => setDesktopDetailActivity(null));
  }, [desktopDetailAnim]);

  useEffect(() => {
    if (!desktopDetailActivity) return;
    Animated.timing(desktopDetailAnim, {
      toValue: 1,
      duration: 240,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [desktopDetailActivity, desktopDetailAnim]);

  const renderMapCard = useCallback(({ item: group }: { item: MapEventGroup }) => {
    if (__DEV__) console.count('[Perf][Render] MapCarouselCard');
    if (group.id === CONTINUED_CAROUSEL_GROUP_ID) {
      return (
        <ContinuedMapCarouselCard
          events={group.events}
          expanded={continuedEventsExpanded}
          imageUrls={resolvedMarkerImageUrls}
          onToggle={() => setContinuedEventsExpanded(expanded => !expanded)}
        />
      );
    }
    if (group.events.length === 1) {
      const activity = group.events[0];
      return (
        <MapPreviewCard
          activity={activity}
          active={selectedEventId === activity.id}
          canManage={canManageEventItem(activity)}
          onEdit={handleEditCard}
          onDelete={handleDeleteCard}
          onPress={handlePressCard}
          userId={userId}
        />
      );
    }

    return (
      <MapVenueGroupCard
        group={group}
        active={group.events.some(activity => activity.id === selectedEventId)}
        canManageEventItem={canManageEventItem}
        onEdit={handleEditCard}
        onDelete={handleDeleteCard}
        onPressGroup={handlePressGroup}
        onPressEvent={handlePressGroupedEvent}
      />
    );
  }, [canManageEventItem, continuedEventsExpanded, handleDeleteCard, handleEditCard, handlePressCard, handlePressGroup, handlePressGroupedEvent, resolvedMarkerImageUrls, selectedEventId, userId]);

  const datePickerModal = (
    <Modal
      visible={datePickerOpen}
      transparent
      animationType="fade"
      onRequestClose={() => setDatePickerOpen(false)}
    >
      <TouchableOpacity
        activeOpacity={1}
        style={[styles.filterModalBackdrop, dateAppearance.modalBackdrop]}
        onPress={() => setDatePickerOpen(false)}
      >
        <TouchableOpacity
          activeOpacity={1}
          accessibilityViewIsModal
          style={[styles.filterModal, styles.dateFilterModal, dateAppearance.modal, { paddingBottom: Math.max(14, insets.bottom) }]}
        >
          <View style={[styles.dateModalHandle, dateAppearance.modalHandle]} />
          <View style={[styles.filterModalHeader, styles.dateModalHeader]}>
            <View style={styles.dateModalHeading}>
              <LinearGradient
                colors={theme.isDark ? ['rgba(242,125,38,0.28)', 'rgba(38,208,206,0.14)'] : ['rgba(242,125,38,0.20)', 'rgba(21,155,154,0.10)']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.dateModalIcon}
              >
                <Icon name="calendar" size={20} color={Colors.sunset} />
              </LinearGradient>
              <View>
                <Text style={[styles.dateModalEyebrow, dateAppearance.accentText]}>
                  {selectedDate.toLocaleDateString(({ de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' } as Record<string, string>)[language] ?? 'en-US', { month: 'long', year: 'numeric' })}
                </Text>
                <Text style={[styles.filterModalTitle, styles.dateModalTitle, dateAppearance.primaryText]}>{t('map.selectDate')}</Text>
              </View>
            </View>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={t('common.close')}
              style={[styles.dateModalClose, dateAppearance.modalClose]}
              onPress={() => setDatePickerOpen(false)}
            >
              <Icon name="x" size={20} color={theme.colors.icon} />
            </TouchableOpacity>
          </View>
          <FlatList
            data={dateOptions}
            keyExtractor={option => toLocalDateKey(option)}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.dateOptionsContent}
            renderItem={({ item }) => {
              const key = toLocalDateKey(item);
              const selected = key === toLocalDateKey(selectedDate);
              const dayOffset = Math.round((item.getTime() - today.getTime()) / 86_400_000);
              const relativeLabel = dayOffset === 0 ? t('feed.today') : dayOffset === 1 ? t('feed.tomorrow') : null;
              return (
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  style={[styles.dateOption, dateAppearance.dateOption, selected && styles.dateOptionActive, selected && dateAppearance.dateOptionActive]}
                  onPress={() => {
                    setSelectedDate(item);
                    setDatePickerOpen(false);
                  }}
                >
                  {selected ? <View style={styles.dateOptionAccent} /> : null}
                  <View style={[styles.dateNumberTile, dateAppearance.dateNumberTile, selected && styles.dateNumberTileActive]}>
                    <Text style={[styles.dateNumber, dateAppearance.dateNumber, selected && styles.dateNumberActive]}>{item.getDate()}</Text>
                  </View>
                  <View style={styles.dateOptionCopy}>
                    <View style={styles.dateOptionTitleRow}>
                      <Text style={[styles.dateOptionDay, dateAppearance.primaryText, selected && styles.filterOptionTextActive, selected && dateAppearance.selectedPrimaryText]}>
                        {item.toLocaleDateString(({ de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' } as Record<string, string>)[language] ?? 'en-US', { weekday: 'long' })}
                      </Text>
                      {relativeLabel ? (
                        <View style={[styles.relativeDateBadge, dateAppearance.relativeDateBadge, selected && styles.relativeDateBadgeActive]}>
                          <Text style={[styles.relativeDateText, dateAppearance.accentText, selected && styles.relativeDateTextActive]}>{relativeLabel}</Text>
                        </View>
                      ) : null}
                    </View>
                    <Text style={[styles.dateOptionText, dateAppearance.secondaryText, selected && styles.dateOptionSubtextActive, selected && dateAppearance.selectedSecondaryText]}>
                      {item.toLocaleDateString(({ de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' } as Record<string, string>)[language] ?? 'en-US', { month: 'long', year: 'numeric' })}
                    </Text>
                  </View>
                  <View style={[styles.dateOptionIndicator, dateAppearance.dateOptionIndicator, selected && styles.dateOptionIndicatorActive]}>
                    {selected ? <Icon name="check" size={14} color="#fff" /> : null}
                  </View>
                </TouchableOpacity>
              );
            }}
          />
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );

  if (loading && !feedEvents.length) return <LoadingSpinner label={t('map.loadingMap')} />;

  if (isDesktopWeb) {
    const desktopSurface = theme.isDark ? '#171C1F' : '#FFFFFF';
    const desktopBorder = theme.isDark ? '#30373B' : '#E3DED5';
    return (
      <View style={[styles.desktopMapShell, { backgroundColor: theme.isDark ? '#131719' : '#F7F5F0' }]}>
        <View style={[styles.desktopEventRail, { backgroundColor: desktopSurface, borderColor: desktopBorder }]}>
          <View style={styles.desktopFilters}>
            <View style={styles.desktopDateRow}>
              <TouchableOpacity onPress={() => setSelectedDate(today)} style={[styles.desktopDateButton, dateMode === 'today' && styles.desktopDateButtonActive, { borderColor: desktopBorder }]}>
                <Text style={[styles.desktopDateText, { color: dateMode === 'today' ? '#FFFFFF' : theme.colors.textPrimary }]}>{t('feed.today')}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setSelectedDate(tomorrow)} style={[styles.desktopDateButton, dateMode === 'tomorrow' && styles.desktopDateButtonActive, { borderColor: desktopBorder }]}>
                <Text style={[styles.desktopDateText, { color: dateMode === 'tomorrow' ? '#FFFFFF' : theme.colors.textPrimary }]}>{t('feed.tomorrow')}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setDatePickerOpen(true)} style={[styles.desktopChooseDate, { borderColor: desktopBorder }]} accessibilityHint="Open the date picker">
                <Text style={[styles.desktopDateText, { color: theme.colors.textPrimary }]}>{dateMode === 'custom' ? selectedMapDate.toLocaleDateString() : t('feed.date')}</Text>
                <Icon name="calendar" size={13} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.desktopCategoryRow}>
              {(['all', 'music', 'social', 'culture', 'free'] as const).map(category => {
                const active = desktopCategory === category;
                return <TouchableOpacity key={category} onPress={() => setDesktopCategory(category)} style={[styles.desktopCategoryChip, { borderColor: active ? theme.colors.accentSecondary : desktopBorder, backgroundColor: active ? theme.colors.accentSecondarySoft : 'transparent' }]}><Text style={[styles.desktopCategoryText, { color: active ? theme.colors.accentSecondary : theme.colors.textSecondary }]}>{category[0].toUpperCase() + category.slice(1)}</Text></TouchableOpacity>;
              })}
            </ScrollView>
          </View>
          <ScrollView style={styles.desktopEventList} contentContainerStyle={styles.desktopEventListContent} showsVerticalScrollIndicator={false}>
            <PromotionsSection
              items={mapPromotions}
              desktop={false}
              onPress={openPromotion}
              testID="map-promotions"
            />
            {desktopEvents.map(activity => {
              const active = activity.id === selectedEventId;
              const imageUrl = resolvedMarkerImageUrls[activity.id] || getMarkerImageUrl(activity);
              return (
                <TouchableOpacity key={activity.id} onPress={() => openDesktopDetail(activity)} style={[styles.desktopEventRow, { borderColor: active ? '#F58538' : desktopBorder, backgroundColor: active ? (theme.isDark ? 'rgba(245,133,56,0.12)' : '#FFF7F1') : 'transparent' }]}>
                  {imageUrl ? <Image source={{ uri: imageUrl }} style={styles.desktopEventImage} /> : <View style={[styles.desktopEventImage, { backgroundColor: theme.colors.surfaceElevated }]} />}
                  <View style={styles.desktopEventCopy}>
                    <Text numberOfLines={1} style={[styles.desktopEventTitle, { color: theme.colors.textPrimary }]}>{activity.title}</Text>
                    <Text numberOfLines={1} style={[styles.desktopEventMeta, { color: theme.colors.textSecondary }]}>{[activity.startTime, activity.endTime].filter(Boolean).join(' – ') || t('event.tonight')}</Text>
                    <Text numberOfLines={1} style={[styles.desktopEventMeta, { color: theme.colors.textSecondary }]}>{activity.venueName || activity.locationName || activity.address || activity.city}</Text>
                  </View>
                  <TouchableOpacity onPress={() => openDesktopDetail(activity)} style={styles.desktopEventArrow} accessibilityLabel={t('event.viewDetails', 'View event details')}><Icon name="chevron-right" size={18} color="#F58538" /></TouchableOpacity>
                </TouchableOpacity>
              );
            })}
            {!desktopEvents.length ? <Text style={[styles.desktopEmptyText, { color: theme.colors.textSecondary }]}>{t('map.noMappedEvents')}</Text> : null}
          </ScrollView>
        </View>
        <View style={styles.desktopMapCanvas}>
          <WebView ref={mapRef as any} style={StyleSheet.absoluteFill} source={{ html: mapHtml, baseUrl: 'https://tonyte.app' }} originWhitelist={['*']} javaScriptEnabled domStorageEnabled onMessage={handleMapMessage} showsHorizontalScrollIndicator={false} showsVerticalScrollIndicator={false} bounces={false} />
          <TouchableOpacity activeOpacity={0.86} style={[styles.desktopRecenter, { backgroundColor: desktopSurface, borderColor: desktopBorder }]} onPress={handleRecenterToMe}><Icon name="crosshair" size={18} color={theme.colors.textPrimary} /></TouchableOpacity>
          {desktopDetailActivity ? (
            <DesktopEventDetailPanel
              activity={desktopDetailActivity}
              anim={desktopDetailAnim}
              desktopSurface={desktopSurface}
              desktopBorder={desktopBorder}
              userId={userId}
              canManage={canManageEventItem(desktopDetailActivity)}
              onClose={closeDesktopDetail}
              onEdit={handleEditCard}
              onDelete={handleDeleteCard}
              onViewFullPage={handlePressCard}
            />
          ) : null}
        </View>
        {datePickerModal}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <TonyteBackground style={StyleSheet.absoluteFill} />
      {/* TS ref objects are invariant, so mapRef's minimal shared
          TonyteMapViewRef type needs a narrow cast on the ref prop below
          rather than widening mapRef's own type. */}
      <WebView
        ref={mapRef as any}
        style={StyleSheet.absoluteFill}
        source={{ html: mapHtml, baseUrl: 'https://tonyte.app' }}
        originWhitelist={['*']}
        javaScriptEnabled
        domStorageEnabled
        onMessage={handleMapMessage}
        onError={(event) => {
          console.warn('[Tonyte][RN][Map] WebView error', event.nativeEvent);
        }}
        onHttpError={(event) => {
          console.warn('[Tonyte][RN][Map] WebView HTTP error', event.nativeEvent);
        }}
        androidLayerType="hardware"
        mixedContentMode="always"
        setSupportMultipleWindows={false}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        bounces={false}
      />

      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <LinearGradient
          colors={['rgba(0,0,0,0.14)', 'rgba(0,0,0,0.04)', 'rgba(0,0,0,0)']}
          style={styles.topVignette}
        />
        <LinearGradient
          colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0.05)', 'rgba(0,0,0,0.18)']}
          style={styles.bottomVignette}
        />
        <AmbientGlow tone="teal" size={290} intensity={0.45} style={styles.mapTealGlow} />
        {selectedEvent ? <AmbientGlow tone="orange" size={330} intensity={0.5} style={styles.mapOrangeGlow} /> : null}
      </View>

      {/* Map header overlay */}
      <View style={[styles.mapHeader, { paddingTop: insets.top + Spacing.sm }]}>
        <View style={styles.mapTopRow}>
          <TouchableOpacity
            activeOpacity={0.86}
            style={styles.dateFilterButton}
            onPress={() => setCityPickerOpen(true)}
          >
            <Icon name="map-pin" size={13} color="rgba(255,255,255,0.78)" />
            <Text style={styles.dateFilterText} numberOfLines={1}>{selectedCityLabel}</Text>
            <Icon name="chevron-down" size={14} color="rgba(255,255,255,0.78)" />
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.86}
            style={styles.dateFilterButton}
            onPress={() => setDatePickerOpen(true)}
          >
            <Icon name="calendar" size={13} color="rgba(255,255,255,0.78)" />
            <Text style={styles.dateFilterText}>
              {dateMode === 'today' ? t('feed.today') : dateMode === 'tomorrow' ? t('feed.tomorrow') : selectedMapDate.toLocaleDateString(({ de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' } as Record<string, string>)[language] ?? 'en-US', { month: 'short', day: 'numeric' })}
            </Text>
            <Icon name="chevron-down" size={14} color="rgba(255,255,255,0.78)" />
          </TouchableOpacity>
        </View>
      </View>

      {mapPromotions.length ? (
        <View style={[styles.mapPromotionsTop, { top: insets.top + 62 }]}>
          <PromotionsSection
            items={mapPromotions}
            desktop={false}
            onPress={openPromotion}
            testID="map-promotions"
          />
        </View>
      ) : null}

      <TouchableOpacity
        activeOpacity={0.86}
        style={styles.discoverArrow}
        onPress={onGoToDiscover}
      >
        <Icon name="arrow-right" size={23} color="#cffafe" />
      </TouchableOpacity>

      <TouchableOpacity
        activeOpacity={0.86}
        style={styles.recenterButton}
        onPress={handleRecenterToMe}
        accessibilityLabel={t('map.recenterToMyLocation', 'Recenter to my location')}
      >
        <Icon name="crosshair" size={19} color={markerData ? '#7dd3fc' : 'rgba(255,255,255,0.7)'} />
      </TouchableOpacity>

      {activeSharedLocation ? (
        <View style={[styles.sharedLocationPill, { top: insets.top + (mapPromotions.length ? 180 : 108) }]}>
          <View style={styles.sharedLocationDot} />
          <Text style={styles.sharedLocationText} numberOfLines={1}>
            {activeSharedLocation.venueName || activeSharedLocation.address || t('chat.sharedLocationLabel')}
          </Text>
          <TouchableOpacity
            activeOpacity={0.8}
            style={styles.sharedLocationDismiss}
            onPress={handleDismissSharedLocation}
            accessibilityLabel={t('map.dismissSharedLocation', 'Dismiss shared location')}
          >
            <Icon name="x" size={14} color="rgba(255,255,255,0.85)" />
          </TouchableOpacity>
        </View>
      ) : null}

      <View style={[styles.carouselDock, { bottom: insets.bottom + 92 }]}>
        {carouselEvents.length ? (
          <>
            {!markerEvents.length ? (
              <View pointerEvents="none" style={styles.nearbyMarkersEmpty}>
                <Icon name="map-pin" size={12} color="rgba(255,255,255,0.72)" />
                <Text style={styles.nearbyMarkersEmptyText}>{t('map.noMappedEventsNearby', 'No mapped events nearby')}</Text>
              </View>
            ) : null}
            <TouchableOpacity
              activeOpacity={0.86}
              style={styles.carouselToggle}
              onPress={() => setCardsCollapsed(value => !value)}
            >
              <Text style={styles.carouselToggleText}>{cardsCollapsed ? t('map.showEvents') : t('map.hide')}</Text>
              <Icon name={cardsCollapsed ? 'chevron-up' : 'chevron-down'} size={12} color="rgba(255,255,255,0.8)" />
            </TouchableOpacity>
            {!cardsCollapsed && (
              <View style={styles.carouselGlass}>
                {continuedEventsExpanded && continuedCarouselGroups.length ? (
                  <View style={styles.continuedEventsPopover}>
                    <View style={styles.continuedEventsPopoverHeader}>
                      <Text style={styles.continuedEventsPopoverTitle}>Still going tonyte</Text>
                      <Text style={styles.continuedEventsPopoverCount}>{continuedCarouselGroups.length} events</Text>
                    </View>
                    <ScrollView style={styles.continuedEventsList} showsVerticalScrollIndicator={false} nestedScrollEnabled>
                      {continuedCarouselGroups.map(group => {
                        const activity = group.events[0];
                        const imageUrl = resolvedMarkerImageUrls[activity.id] || getMarkerImageUrl(activity);
                        return (
                          <TouchableOpacity
                            key={activity.id}
                            activeOpacity={0.84}
                            style={styles.continuedEventRow}
                            onPress={() => handlePressCard(activity)}
                          >
                            {imageUrl ? <Image source={{ uri: imageUrl }} style={styles.continuedEventRowImage} /> : <View style={styles.continuedEventRowImageFallback}><Icon name="star" size={12} color="#FFD29F" /></View>}
                            <View style={styles.continuedEventRowCopy}>
                              <Text style={styles.continuedEventRowTitle} numberOfLines={1}>{activity.title}</Text>
                              <Text style={styles.continuedEventRowMeta} numberOfLines={1}>
                                {activity.endTime ? `Until ${activity.endTime}` : 'Still happening'} · {activity.venueName || activity.locationName || activity.city}
                              </Text>
                            </View>
                            <Icon name="chevron-right" size={15} color="rgba(255,255,255,0.68)" />
                          </TouchableOpacity>
                        );
                      })}
                    </ScrollView>
                  </View>
                ) : null}
                <FlatList
                  ref={carouselRef}
                  data={visibleCarouselGroups}
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.carouselList}
                  decelerationRate="fast"
                  snapToInterval={MAP_CARD_SNAP_INTERVAL}
                  snapToAlignment="start"
                  keyExtractor={keyExtractorMapCard}
                  renderItem={renderMapCard}
                  initialNumToRender={5}
                  maxToRenderPerBatch={5}
                  windowSize={3}
                  removeClippedSubviews
                  getItemLayout={(_, index) => ({
                    length: MAP_CARD_SNAP_INTERVAL,
                    offset: MAP_CARD_SNAP_INTERVAL * index,
                    index,
                  })}
                  onMomentumScrollEnd={handleCarouselMomentumEnd}
                  onScrollToIndexFailed={({ index }) => {
                    pendingCarouselIndexRef.current = index;
                    setTimeout(() => {
                      carouselRef.current?.scrollToIndex({
                        index,
                        animated: true,
                        viewPosition: 0.5,
                      });
                      pendingCarouselIndexRef.current = null;
                    }, 80);
                  }}
                />
              </View>
            )}
          </>
        ) : (
          <View style={styles.emptyMapCard}>
            <Icon name="map-pin" size={14} color={Colors.sunset} />
              <Text style={styles.emptyMapText}>{t('map.noMappedEvents')}</Text>
          </View>
        )}
      </View>

      {datePickerModal}

      <Modal
        visible={cityPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setCityPickerOpen(false)}
      >
        <TouchableOpacity
          activeOpacity={1}
          style={styles.filterModalBackdrop}
          onPress={() => setCityPickerOpen(false)}
        >
          <TouchableOpacity activeOpacity={1} style={styles.filterModal}>
            <View style={styles.filterModalHeader}>
              <Text style={styles.filterModalTitle}>{t('map.selectCity')}</Text>
              <TouchableOpacity onPress={() => setCityPickerOpen(false)}>
                <Icon name="x" size={18} color="rgba(255,255,255,0.72)" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={cityOptions}
              keyExtractor={option => option.key}
              showsVerticalScrollIndicator={false}
              renderItem={({ item }) => {
                const selected = item.key === selectedCityKey;
                return (
                  <TouchableOpacity
                    style={[styles.filterOption, selected && styles.filterOptionActive]}
                    onPress={() => {
                      setSelectedCityKey(item.key);
                      setCityPickerOpen(false);
                    }}
                  >
                    <Text style={[styles.filterOptionText, selected && styles.filterOptionTextActive]}>{item.label}</Text>
                  </TouchableOpacity>
                );
              }}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

    </View>
  );
};

const createMapDateThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  modalBackdrop: daylightStyle(theme, { backgroundColor: 'rgba(52,43,34,0.34)' }),
  modal: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
    ...theme.shadows.elevated,
  }),
  modalHandle: daylightStyle(theme, { backgroundColor: theme.colors.borderStrong }),
  modalClose: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  dateOption: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  dateOptionActive: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: 'rgba(242,125,38,0.62)',
  }),
  dateNumberTile: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  dateNumber: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  primaryText: daylightStyle(theme, { color: theme.colors.textPrimary }),
  selectedPrimaryText: daylightStyle(theme, { color: theme.colors.textPrimary }),
  secondaryText: daylightStyle(theme, { color: theme.colors.textSecondary }),
  selectedSecondaryText: daylightStyle(theme, { color: theme.colors.textSecondary }),
  accentText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  relativeDateBadge: daylightStyle(theme, { backgroundColor: theme.colors.accentSecondarySoft }),
  dateOptionIndicator: daylightStyle(theme, { borderColor: theme.colors.borderStrong }),
});

const createMapCarouselThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  carouselCard: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
  }),
  carouselTypeChip: daylightStyle(theme, {
    backgroundColor: 'rgba(255,249,239,0.90)',
    borderColor: 'rgba(145,118,88,0.24)',
  }),
  carouselType: daylightStyle(theme, {
    color: '#514B45',
  }),
  carouselIconBtn: daylightStyle(theme, {
    backgroundColor: theme.colors.mediaActionBackground,
    borderColor: theme.colors.mediaActionBorder,
  }),
  mapManageButton: daylightStyle(theme, {
    backgroundColor: 'rgba(255,249,239,0.90)',
    borderColor: 'rgba(145,118,88,0.24)',
  }),
  mapManageButtonText: daylightStyle(theme, {
    color: theme.colors.textPrimary,
  }),
  carouselTitle: daylightStyle(theme, {
    color: '#FFFDF8',
    textShadowColor: 'rgba(18,10,5,0.78)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  }),
  carouselTime: daylightStyle(theme, {
    color: 'rgba(255,250,242,0.90)',
  }),
  carouselLocation: daylightStyle(theme, {
    color: 'rgba(255,250,242,0.82)',
  }),
  groupCountPill: daylightStyle(theme, {
    borderColor: 'rgba(255,255,255,0.68)',
    backgroundColor: '#F29A50',
  }),
  groupEventRow: daylightStyle(theme, {
    backgroundColor: 'rgba(255,249,239,0.90)',
    borderColor: 'rgba(145,118,88,0.22)',
  }),
  groupEventTimePill: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  groupEventTimeText: daylightStyle(theme, {
    color: theme.colors.accentSecondary,
  }),
  groupEventTitle: daylightStyle(theme, {
    color: theme.colors.textPrimary,
  }),
  groupEventMeta: daylightStyle(theme, {
    color: theme.colors.textSecondary,
  }),
  groupManageButton: daylightStyle(theme, {
    backgroundColor: theme.colors.mediaActionBackground,
    borderColor: theme.colors.mediaActionBorder,
  }),
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.bg,
    overflow: 'hidden',
  },
  desktopMapShell: { flex: 1, flexDirection: 'row', overflow: 'hidden' },
  desktopEventRail: { width: 390, minWidth: 340, borderRightWidth: 1 },
  desktopFilters: { paddingHorizontal: 18, paddingTop: 18, paddingBottom: 12, gap: 13 },
  desktopDateRow: { flexDirection: 'row', gap: 8 },
  desktopDateButton: { height: 36, minWidth: 78, borderWidth: 1, borderRadius: 9, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 13 },
  desktopDateButtonActive: { backgroundColor: '#F58538', borderColor: '#F58538' },
  desktopChooseDate: { height: 36, flex: 1, borderWidth: 1, borderRadius: 9, paddingHorizontal: 11, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  desktopDateText: { fontSize: 12, fontWeight: '600' },
  desktopCategoryRow: { gap: 7 },
  desktopCategoryChip: { height: 29, borderWidth: 1, borderRadius: 15, paddingHorizontal: 12, alignItems: 'center', justifyContent: 'center' },
  desktopCategoryText: { fontSize: 11, fontWeight: '600' },
  desktopEventList: { flex: 1 },
  desktopEventListContent: { paddingHorizontal: 14, paddingBottom: 22 },
  desktopEventRow: { minHeight: 82, borderBottomWidth: 1, borderRadius: 9, padding: 9, flexDirection: 'row', alignItems: 'center', gap: 11 },
  desktopEventImage: { width: 76, height: 58, borderRadius: 7 },
  desktopEventCopy: { flex: 1, minWidth: 0, gap: 3 },
  desktopEventTitle: { fontSize: 13, fontWeight: '700' },
  desktopEventMeta: { fontSize: 11, lineHeight: 14 },
  desktopEventArrow: { width: 28, height: 38, alignItems: 'center', justifyContent: 'center' },
  desktopEmptyText: { padding: 24, textAlign: 'center', fontSize: 13 },
  desktopMapCanvas: { flex: 1, minWidth: 0, position: 'relative' },
  desktopRecenter: { position: 'absolute', right: 18, top: 18, width: 40, height: 40, borderRadius: 8, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  desktopDetailPanel: {
    position: 'absolute', top: 18, right: 18, bottom: 18, width: 380,
    borderRadius: 18, borderWidth: 1, overflow: 'hidden', ...Shadow.strong,
  },
  desktopDetailScroll: { flexGrow: 1 },
  desktopDetailHero: { height: 190, position: 'relative' },
  desktopDetailClose: {
    position: 'absolute', top: 12, right: 12, width: 30, height: 30, borderRadius: 15,
    backgroundColor: 'rgba(3,7,15,0.5)', alignItems: 'center', justifyContent: 'center',
  },
  desktopDetailHeroChip: {
    position: 'absolute', left: 14, bottom: 14, paddingHorizontal: 10, paddingVertical: 5,
    borderRadius: Radius.pill, backgroundColor: 'rgba(242,125,38,0.85)',
  },
  desktopDetailHeroChipText: { color: '#fff', fontSize: 10, fontWeight: '800', letterSpacing: 0.6, textTransform: 'uppercase' },
  desktopDetailBody: { padding: 18, gap: 10 },
  desktopDetailTitle: { fontSize: 18, lineHeight: 24, fontWeight: '800' },
  desktopDetailMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  desktopDetailMetaText: { flex: 1, fontSize: 13 },
  desktopDetailDescription: { fontSize: 13, lineHeight: 19, marginTop: 4 },
  desktopDetailTagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 2 },
  desktopDetailTag: { paddingHorizontal: 9, paddingVertical: 4, borderRadius: Radius.pill, borderWidth: 1 },
  desktopDetailTagText: { fontSize: 11 },
  desktopDetailInterestBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7,
    height: 42, borderRadius: 12, borderWidth: 1.5, marginTop: 6,
  },
  desktopDetailInterestText: { fontSize: 13, fontWeight: '700' },
  desktopDetailManageRow: { flexDirection: 'row', gap: 8, marginTop: 2 },
  desktopDetailManageBtn: { flex: 1, height: 36, borderRadius: 10, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  desktopDetailManageText: { fontSize: 12, fontWeight: '700' },
  desktopDetailFullPageLink: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    marginTop: 8, paddingVertical: 6,
  },
  desktopDetailFullPageText: { color: '#F58538', fontSize: 12, fontWeight: '700' },
  mapHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  topVignette: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 112,
  },
  bottomVignette: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 176,
  },
  mapTealGlow: {
    left: -150,
    bottom: 36,
  },
  mapOrangeGlow: {
    right: -170,
    top: 34,
  },
  mapTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.base,
  },
  dateFilterButton: {
    height: 40,
    minWidth: 116,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: Spacing.base,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(0,0,0,0.3)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 16 },
    shadowOpacity: 0.28,
    shadowRadius: 44,
    elevation: 12,
  },
  dateFilterText: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 12,
    fontWeight: Typography.bold,
  },
  roundTopButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(0,0,0,0.3)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 16 },
    shadowOpacity: 0.28,
    shadowRadius: 44,
    elevation: 12,
  },
  mapHeaderInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: Spacing.base,
    backgroundColor: Colors.bgGlass,
    borderRadius: Radius.xl,
    paddingHorizontal: Spacing.base,
    paddingVertical: Spacing.md,
    borderWidth: 1,
    borderColor: Colors.border,
    ...Shadow.card,
  },
  mapTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.bold,
  },
  eventCount: {
    backgroundColor: Colors.sunset + '22',
    borderRadius: Radius.pill,
    paddingHorizontal: Spacing.md,
    paddingVertical: 4,
  },
  eventCountText: {
    color: Colors.sunset,
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  markerWrap: {
    width: 58,
    height: 72,
    alignItems: 'center',
  },
  markerWrapActive: {
    transform: [{ scale: 1.08 }],
  },
  markerBody: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderBottomLeftRadius: 5,
    transform: [{ rotate: '-45deg' }],
    overflow: 'hidden',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.72)',
    backgroundColor: '#111',
    shadowOffset: { width: 0, height: 7 },
    shadowOpacity: 0.34,
    shadowRadius: 14,
    elevation: 8,
  },
  markerBodyActive: {
    borderColor: 'rgba(230,255,251,0.94)',
    shadowOpacity: 0.58,
    shadowRadius: 16,
    elevation: 12,
  },
  markerImage: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ rotate: '45deg' }, { scale: 1.42 }],
  },
  markerSheen: {
    ...StyleSheet.absoluteFill,
    borderRadius: 26,
  },
  markerTail: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 4,
    shadowColor: Colors.turquoise,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.32,
    shadowRadius: 9,
    elevation: 4,
  },
  debugCenterMarker: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.sunset,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.82)',
    shadowColor: Colors.sunset,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.6,
    shadowRadius: 14,
    elevation: 10,
  },
  discoverArrow: {
    position: 'absolute',
    right: 14,
    top: '50%',
    width: 48,
    height: 48,
    marginTop: -24,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(165,243,252,0.2)',
    backgroundColor: 'rgba(7,17,28,0.72)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: Colors.turquoise,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 18,
    elevation: 12,
  },
  recenterButton: {
    position: 'absolute',
    right: 14,
    top: '50%',
    width: 40,
    height: 40,
    marginTop: 38,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(125,211,252,0.2)',
    backgroundColor: 'rgba(7,17,28,0.72)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 10,
  },
  sharedLocationPill: {
    position: 'absolute',
    left: 14,
    right: 68,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.32)',
    backgroundColor: 'rgba(7,17,28,0.86)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.22,
    shadowRadius: 10,
    elevation: 10,
  },
  sharedLocationDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
    backgroundColor: Colors.turquoise,
  },
  sharedLocationText: {
    flex: 1,
    color: 'rgba(255,255,255,0.92)',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  sharedLocationDismiss: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  carouselDock: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  mapPromotionsTop: {
    position: 'absolute',
    left: 0,
    right: 0,
    width: '100%',
    paddingHorizontal: Spacing.base,
    zIndex: 4,
  },
  carouselToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.md,
    alignSelf: 'center',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(0,0,0,0.3)',
    marginBottom: 8,
  },
  carouselToggleText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 11,
    fontWeight: Typography.semibold,
  },
  carouselGlass: {
    width: '100%',
    maxHeight: 430,
    backgroundColor: 'transparent',
  },
  continuedEventsPopover: {
    maxHeight: 188,
    marginHorizontal: Spacing.base,
    padding: 9,
    overflow: 'hidden',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.30)',
    backgroundColor: 'rgba(5,13,23,0.96)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.34,
    shadowRadius: 18,
    elevation: 14,
  },
  continuedEventsPopoverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 5,
    paddingBottom: 7,
  },
  continuedEventsPopoverTitle: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: Typography.extrabold,
  },
  continuedEventsPopoverCount: {
    color: '#6EE7E5',
    fontSize: 9,
    fontWeight: Typography.bold,
  },
  continuedEventsList: {
    maxHeight: 145,
  },
  continuedEventRow: {
    minHeight: 43,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingHorizontal: 6,
    paddingVertical: 5,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
  },
  continuedEventRowImage: {
    width: 33,
    height: 33,
    borderRadius: 9,
  },
  continuedEventRowImageFallback: {
    width: 33,
    height: 33,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(242,125,38,0.16)',
  },
  continuedEventRowCopy: {
    flex: 1,
    minWidth: 0,
  },
  continuedEventRowTitle: {
    color: '#FFFFFF',
    fontSize: 11,
    lineHeight: 14,
    fontWeight: Typography.bold,
  },
  continuedEventRowMeta: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: 9,
    lineHeight: 12,
    marginTop: 1,
  },
  carouselList: {
    paddingVertical: 12,
    paddingLeft: Spacing.base,
    paddingRight: Spacing.base,
  },
  nearbyMarkersEmpty: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    alignSelf: 'center',
    marginBottom: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.11)',
    backgroundColor: 'rgba(4,8,15,0.58)',
  },
  nearbyMarkersEmptyText: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: 11,
    fontWeight: Typography.semibold,
  },
  carouselFrameSpacing: {
    marginRight: MAP_CARD_GAP,
  },
  continuedCarouselCard: {
    width: MAP_CARD_WIDTH,
    height: CAROUSEL_CARD_HEIGHT,
    padding: Spacing.md,
    overflow: 'hidden',
    justifyContent: 'space-between',
    borderRadius: 16.8,
    backgroundColor: '#101722',
  },
  continuedCardGlow: {
    position: 'absolute',
    right: -28,
    bottom: -44,
    width: 130,
    height: 130,
    borderRadius: 65,
    backgroundColor: 'rgba(242,125,38,0.16)',
  },
  continuedCardTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  continuedCardLabel: {
    height: 23,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,210,159,0.28)',
    backgroundColor: 'rgba(255,255,255,0.07)',
  },
  continuedCardLabelText: {
    color: '#FFD29F',
    fontSize: 8,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.1,
  },
  continuedCardCount: {
    minWidth: 27,
    height: 27,
    paddingHorizontal: 7,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.42)',
    backgroundColor: 'rgba(38,208,206,0.13)',
  },
  continuedCardCountText: {
    color: '#6EE7E5',
    fontSize: 11,
    fontWeight: Typography.extrabold,
  },
  continuedCardBottomRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
  },
  continuedCardCopy: {
    flex: 1,
    minWidth: 0,
  },
  continuedCardTitle: {
    color: '#FFFFFF',
    fontSize: 17,
    lineHeight: 21,
    fontWeight: Typography.extrabold,
  },
  continuedCardHint: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: 9,
    lineHeight: 12,
    marginTop: 2,
  },
  continuedCardImages: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 1,
  },
  continuedCardImage: {
    width: 29,
    height: 29,
    borderRadius: 9,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.72)',
    backgroundColor: '#101722',
  },
  continuedCardImageOverlap: {
    marginLeft: -9,
  },
  carouselCard: {
    width: MAP_CARD_WIDTH,
    borderRadius: 16.8,
    overflow: 'hidden',
    backgroundColor: 'rgb(3,7,15)',
  },
  groupCarouselCard: {
    width: MAP_CARD_WIDTH,
    minHeight: 154,
    maxHeight: 220,
    borderRadius: 16.8,
    overflow: 'hidden',
    backgroundColor: 'rgb(3,7,15)',
  },
  groupCardImage: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  groupCardContent: {
    padding: Spacing.md,
    gap: 9,
  },
  groupCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: Spacing.sm,
  },
  groupCardTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  groupCountPill: {
    minWidth: 29,
    height: 29,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.42)',
    backgroundColor: 'rgba(251,146,60,0.92)',
  },
  groupCountText: {
    color: '#120a04',
    fontSize: 13,
    lineHeight: 15,
    fontWeight: Typography.extrabold,
  },
  groupEventStack: {
    gap: 7,
  },
  groupEventRow: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 7,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    backgroundColor: 'rgba(6,10,18,0.6)',
  },
  groupEventTimePill: {
    minWidth: 48,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 7,
    backgroundColor: 'rgba(38,208,206,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
  },
  groupEventTimeText: {
    color: '#cffafe',
    fontSize: 10,
    lineHeight: 12,
    fontWeight: Typography.extrabold,
  },
  groupEventTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  groupEventTitle: {
    color: Colors.textPrimary,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: Typography.bold,
  },
  groupEventMeta: {
    color: 'rgba(255,255,255,0.48)',
    fontSize: 9,
    lineHeight: 12,
    marginTop: 1,
  },
  groupManageActions: {
    flexDirection: 'row',
    gap: 4,
  },
  groupManageButton: {
    width: 25,
    height: 25,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(0,0,0,0.28)',
  },
  carouselImage: {
    width: '100%',
    height: '100%',
  },
  carouselContent: {
    position: 'absolute',
    inset: 0,
    padding: Spacing.md,
    justifyContent: 'space-between',
  },
  carouselTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: Spacing.sm,
  },
  carouselTypeChip: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
  },
  carouselType: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: 9,
    fontWeight: Typography.bold,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
  },
  carouselIconBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(8,12,20,0.55)',
  },
  mapManageActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 7,
    marginTop: 2,
  },
  mapManageButton: {
    minHeight: 30,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 11,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(10,14,22,0.55)',
  },
  mapManageButtonText: {
    color: '#fff',
    fontSize: 11,
    lineHeight: 13,
    fontWeight: Typography.extrabold,
  },
  mapManageDeleteText: {
    color: Colors.sunset,
  },
  carouselTitle: {
    color: Colors.textPrimary,
    fontSize: 13,
    fontWeight: Typography.extrabold,
    lineHeight: 17,
    letterSpacing: 0.1,
  },
  carouselTime: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 10,
    marginTop: 2,
  },
  carouselLocationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  carouselLocation: {
    color: 'rgba(255,255,255,0.42)',
    fontSize: 10,
    flex: 1,
  },
  emptyMapCard: {
    marginHorizontal: Spacing.base,
    minHeight: 54,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.sm,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(4,8,15,0.72)',
    paddingHorizontal: Spacing.base,
  },
  emptyMapText: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  filterModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.58)',
    justifyContent: 'flex-end',
  },
  filterModal: {
    maxHeight: '64%',
    marginHorizontal: 14,
    marginBottom: 20,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: '#0b111d',
    padding: 14,
    ...Shadow.strong,
  },
  filterModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  filterModalTitle: {
    color: Colors.textPrimary,
    fontSize: 16,
    fontWeight: Typography.extrabold,
  },
  dateFilterModal: {
    maxHeight: '72%',
    marginHorizontal: 10,
    marginBottom: 10,
    borderRadius: 26,
    paddingHorizontal: 16,
    paddingTop: 9,
  },
  dateModalHandle: {
    width: 42,
    height: 4,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.22)',
    alignSelf: 'center',
    marginBottom: 13,
  },
  dateModalHeader: { marginBottom: 16 },
  dateModalHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  dateModalIcon: {
    width: 46,
    height: 46,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.28)',
  },
  dateModalEyebrow: {
    color: Colors.turquoise,
    fontSize: 10,
    lineHeight: 13,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    fontWeight: Typography.extrabold,
  },
  dateModalTitle: { fontSize: 22, lineHeight: 27 },
  dateModalClose: {
    width: 40,
    height: 40,
    borderRadius: Radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
  },
  dateOptionsContent: { paddingBottom: 2 },
  dateOption: {
    minHeight: 68,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 9,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
  },
  dateOptionActive: {
    borderColor: 'rgba(242,125,38,0.72)',
    backgroundColor: 'rgba(242,125,38,0.13)',
    ...Platform.select({
      ios: {
        shadowColor: Colors.sunset,
        shadowOpacity: 0.18,
        shadowRadius: 12,
        shadowOffset: { width: 0, height: 2 },
      },
      android: { elevation: 0 },
    }),
  },
  dateOptionAccent: {
    position: 'absolute', left: 0, top: 15, bottom: 15, width: 3,
    borderTopRightRadius: Radius.pill,
    borderBottomRightRadius: Radius.pill,
    backgroundColor: Colors.sunset,
  },
  dateNumberTile: {
    width: 46,
    height: 46,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(38,208,206,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.2)',
  },
  dateNumberTileActive: {
    backgroundColor: Colors.sunset,
    borderColor: 'rgba(255,255,255,0.22)',
  },
  dateNumber: { color: Colors.turquoise, fontSize: 19, fontWeight: Typography.extrabold },
  dateNumberActive: { color: '#fff' },
  dateOptionCopy: { flex: 1, marginLeft: 12 },
  dateOptionTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  dateOptionDay: {
    color: Colors.textPrimary,
    fontSize: 15,
    lineHeight: 19,
    fontWeight: Typography.bold,
  },
  dateOptionText: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: 12,
    lineHeight: 17,
    fontWeight: Typography.medium,
  },
  dateOptionSubtextActive: { color: 'rgba(255,255,255,0.72)' },
  relativeDateBadge: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(38,208,206,0.12)',
  },
  relativeDateBadgeActive: { backgroundColor: 'rgba(242,125,38,0.22)' },
  relativeDateText: {
    color: Colors.turquoise,
    fontSize: 9,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    fontWeight: Typography.extrabold,
  },
  relativeDateTextActive: { color: '#ffb57f' },
  dateOptionIndicator: {
    width: 24,
    height: 24,
    marginLeft: 8,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dateOptionIndicatorActive: {
    backgroundColor: Colors.sunset,
    borderColor: Colors.sunset,
  },
  quickDateRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 10,
  },
  quickDateButton: {
    flex: 1,
    minHeight: 42,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterOption: {
    minHeight: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    paddingHorizontal: 13,
    paddingVertical: 8,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  filterOptionActive: {
    borderColor: 'rgba(242,125,38,0.62)',
    backgroundColor: 'rgba(242,125,38,0.14)',
  },
  filterOptionEyebrow: {
    color: Colors.turquoise,
    fontSize: 12,
    fontWeight: Typography.extrabold,
    textTransform: 'uppercase',
  },
  filterOptionText: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: 14,
    fontWeight: Typography.bold,
  },
  filterOptionTextActive: {
    color: Colors.textPrimary,
  },
});

export default MapScreen;
